import { DynamicIcon } from '@/components/DynamicIcon'
import { validateSubstringValueLength } from '@/components/filterable-search-bar'
import { useClearFilters } from '@/components/page-context/context'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { FilteredCountIndicator } from '@/components/ui/filtered-count-indicator'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ListSkeleton } from '@/components/ui/skeletons'
import { TableCheckbox } from '@/components/ui/table-checkbox'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { SEARCH_SECTION_STORAGE_KEY } from '@/config/storageKeys'
import {
	TIME_RANGE_OPTIONS,
	convertMsToReadable,
	getTypeFromClickhouseToTS,
	isWithinTimeRange,
	validateInputValue,
} from '@/config/utils'
import { getEffectiveFilters } from '@/hooks/applyFilterLogic'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useVirtualOptionRows } from '@/hooks/useOptionListVirtualizer'
import { useUrlFilters } from '@/hooks/useUrlFilters'
import { filterLimitFlags, getInitialFiltersFromUrl, getRouteSection } from '@/lib/filterUtils'
import { cn } from '@/lib/utils'
import type { ColumnValueSource, ColumnValueSourcePage } from '@/types/columnValueSource'
import { useInfiniteQuery, useQueries } from '@tanstack/react-query'
import { Check, ChevronDown, ChevronLeft, X } from 'lucide-react'
import type { IconName } from 'lucide-react/dynamic'
import * as React from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'
import { Separator } from '../ui/separator'

const SEARCH_DROPDOWN_WIDTH_PX = 300

/** Icon + colour for one known value of a column. */
export interface FilterValueDecoration {
	icon?: string
	color?: string
}

export interface FilterOption {
	column: string
	operator: 'contains' | 'equals' | 'notEquals' | 'is_null' | 'is_not_null' | 'timeRange' | '<' | '<=' | '>' | '>='
	value: string
	isNullOperator?: boolean
}

/** Max values applied in one "Select All" click for a server-backed column (backend per_page ceiling). */
const SELECT_ALL_MAX = 1000
const FILTER_SUMMARY_VALUE_LIMIT = 2
interface SearchBarProps {
	value: string
	onChange: (value: string) => void
	placeholder?: string
	className?: string
	disabled?: boolean
	showClearButton?: boolean
	insideModal?: boolean
	columns: Array<{
		id: string
		label: string
		formatValue?: (value: unknown) => string
		isTimestamp?: boolean
		isRadio?: boolean
		dataType?: string
	}>
	data?: Record<string, unknown>[]
	// Per-column value suggestions sourced server-side (keyed by column id). When provided for a column,
	// it overrides the values derived from `data` for that column. Omitted columns fall back to `data`.
	columnValueOptions?: Record<string, (string | number)[]>
	/** Per-column badge look for known values (a derived column's states), keyed by column then value. */
	columnValueDecorations?: Record<string, Record<string, FilterValueDecoration>>
	// Optional server-side, lazily-paginated value suggestions. Takes precedence over columnValueOptions/data
	// for the columns it supports. See ColumnValueSource. Used for large fleets (100k+ devices).
	columnValueSource?: ColumnValueSource
	// Opt-in: cap `contains` values to the backend regex byte limit. Only for consumers whose `contains`
	// becomes a server-side regex (device search). Client-side-filtered pages leave this off — their
	// `contains` is an in-memory substring match with no length limit.
	validateSubstringLength?: boolean
	filters?: FilterOption[] // active filter chips managed by the caller (e.g. via useDeviceFilter)
	appliedFilters?: FilterOption[] // overrides filters for display — use to pre-populate chips (e.g. edit mode) without going through hook state
	onFiltersChange?: (filters: FilterOption[]) => void
	enableOperatorChange?: boolean
	columnOperators?: Record<
		string,
		Array<'equals' | 'notEquals' | 'contains' | 'is_null' | 'is_not_null' | 'timeRange' | '<' | '<=' | '>' | '>='>
	>
	orGroups?: string[][]
	allowLogicOperatorChange?: boolean
	onOrGroupsChange?: (orGroups: string[][]) => void
	skipOperatorSelection?: string[]
	customInputColumns?: string[]
	// Columns that apply as a single terminal click (no value step) and render as a value-less, label-only
	// chip removed by clicking it. Use for an all-or-nothing scope entry (e.g. "All devices").
	standaloneColumns?: string[]
	// Columns where the raw typed value is always offered as a selectable suggestion, even when it
	// isn't present in the (client-side) value cache. Use for server-backed search: selecting the
	// free-text entry applies a filter the caller forwards to the backend. Matched on column id.
	freeTextColumns?: string[]
	filteredCount?: number
	totalCount?: number
	isLoading?: boolean
	/** Overrides the indicator's default "Filtered:" label, for a bar that also filters by other fields. */
	filteredCountLabel?: string
	validateInput?: (column: string, value: string) => string | null
	containerTestId?: string
	searchInputTestId?: string
	clearButtonTestId?: string
	filterPopoverTestId?: string
	filteredCountTestId?: string
	filterOptionCheckboxTestId?: (value: string) => string
	/** Test id for a column-selection button in the filter popover, keyed by column id. */
	filterColumnButtonTestId?: (columnId: string) => string
}

function filterValueTestIdSegment(value: string): string {
	return value.replace(/[^a-zA-Z0-9_-]/g, '-')
}

// Initial render window for value options; grown by this step as the user scrolls near the bottom.
// In-memory + virtualized, so growing is render-only (no network) and cheap at any size.
const VALUE_OPTIONS_PAGE_SIZE = 200

// Server-matched values fetched per column for the typed-value suggestion list. Small on purpose —
// one request per searchable column fires in parallel; the per-column drill-down handles deep paging.
const SUGGESTION_VALUES_PER_COLUMN = 20

const VALUE_OPTION_ROW_HEIGHT = 36

/** Virtualized value-options list: renders only the visible rows so large lists stay responsive. */
function VirtualizedValueList({
	values,
	isTimestamp,
	isRadio,
	selectedValues,
	focusedIndex,
	onValueClick,
	onMouseEnter,
	getButtonClassName,
	hiddenCount,
	onReachEnd,
	insideModal,
	getCheckboxTestId,
	valueDecorations,
}: {
	values: string[]
	isTimestamp: boolean
	isRadio?: boolean
	selectedValues: Set<string>
	focusedIndex: number
	onValueClick: (value: string) => void
	onMouseEnter: (index: number) => void
	getButtonClassName: (options?: { isFocused?: boolean; isChecked?: boolean; additionalClasses?: string }) => string
	hiddenCount: number
	// Called when the user scrolls near the bottom, so the caller can grow the rendered window.
	onReachEnd?: () => void
	insideModal: boolean
	getCheckboxTestId: (value: string) => string | undefined
	valueDecorations?: Record<string, FilterValueDecoration>
}) {
	// Values wrap (`break-all` below), so rows are measured rather than fixed-height.
	const rows = useVirtualOptionRows({
		count: values.length,
		rowHeight: VALUE_OPTION_ROW_HEIGHT,
		measure: true,
		getItemKey: index => values[index] ?? index,
		overscan: 12,
		onReachEnd: hiddenCount > 0 ? onReachEnd : undefined,
	})

	// Keep the keyboard-focused row in view (the list is virtualized, so it may not be mounted).
	React.useEffect(() => {
		if (focusedIndex >= 0 && focusedIndex < values.length) {
			rows.scrollToIndex(focusedIndex)
		}
	}, [focusedIndex, values.length, rows.scrollToIndex])

	return (
		<div
			ref={rows.setScrollElement}
			onScroll={rows.handleScroll}
			className={`${insideModal ? 'max-h-[280px]' : 'max-h-[330px]'} overflow-y-auto`}>
			<div style={{ height: `${rows.totalSize}px`, position: 'relative', width: '100%' }}>
				{rows.virtualItems.map(virtualRow => {
					const value = values[virtualRow.index]
					const bgChecked = selectedValues.has(value)
					const comparisonValue = isTimestamp
						? TIME_RANGE_OPTIONS.find(option => option.label === value)?.value || value
						: value
					const valueSelected = isTimestamp ? selectedValues.has(comparisonValue) : bgChecked
					return (
						<div
							key={`value-${value}`}
							data-index={virtualRow.index}
							ref={rows.measureRow}
							style={rows.rowStyle(virtualRow)}>
							<Button
								type='button'
								variant='ghost'
								onClick={() => onValueClick(value)}
								onMouseEnter={() => onMouseEnter(virtualRow.index)}
								className={getButtonClassName({
									isFocused: focusedIndex === virtualRow.index,
									isChecked: bgChecked,
									additionalClasses: 'text-control gap-2 py-1.5 h-auto',
								})}>
								<div className='flex items-center gap-4 min-w-0 flex-1 text-left'>
									{isTimestamp || isRadio ? (
										<input
											type='radio'
											checked={valueSelected}
											readOnly
											data-testid={getCheckboxTestId(value)}
											className='size-(--icon-standalone) shrink-0 border-ring/30 text-foreground focus:ring-0'
										/>
									) : (
										<span className='inline-flex items-center shrink-0'>
											<TableCheckbox checked={valueSelected} readOnly data-testid={getCheckboxTestId(value)} />
										</span>
									)}
									{valueDecorations?.[value]?.icon && (
										<DynamicIcon
											name={valueDecorations[value].icon as IconName}
											className='size-(--icon-standalone) shrink-0'
											style={{ color: valueDecorations[value].color }}
										/>
									)}
									<span className='text-control break-all'>{value}</span>
								</div>
								{valueSelected && (
									<span className='shrink-0 ml-2'>
										<Check className='size-(--icon-standalone) text-muted-foreground' />
									</span>
								)}
							</Button>
						</div>
					)
				})}
			</div>
			{hiddenCount > 0 && (
				<div className='px-4 py-2 text-center text-control text-muted-foreground'>
					+{hiddenCount} more, scroll or type to narrow
				</div>
			)}
		</div>
	)
}

export function FiltersSearchBar<T extends Record<string, unknown>>({
	value,
	onChange,
	placeholder = 'Search...',
	className,
	disabled = false,
	showClearButton = true,
	insideModal = false,
	columns,
	data = [],
	columnValueOptions,
	columnValueDecorations,
	columnValueSource,
	validateSubstringLength = false,
	filters: filtersProp = [],
	appliedFilters,
	onFiltersChange,
	enableOperatorChange = false,
	columnOperators: propColumnOperators = {},
	orGroups = [],
	allowLogicOperatorChange = false,
	onOrGroupsChange,
	skipOperatorSelection = [],
	customInputColumns = [],
	standaloneColumns = [],
	freeTextColumns = [],
	filteredCount,
	totalCount,
	isLoading = false,
	filteredCountLabel,
	validateInput,
	containerTestId,
	searchInputTestId,
	clearButtonTestId,
	filterPopoverTestId,
	filteredCountTestId,
	filterOptionCheckboxTestId,
	filterColumnButtonTestId,
}: SearchBarProps & { data?: T[] }) {
	// appliedFilters overrides the filters prop for display — used to pre-populate chips (e.g. edit mode)
	// without going through the useDeviceFilter pipeline. Falls back to the normal filters prop.
	const filters = appliedFilters !== undefined ? appliedFilters : filtersProp
	const resolveFilterOptionCheckboxTestId = React.useCallback(
		(val: string) =>
			filterOptionCheckboxTestId?.(val) ??
			(filterPopoverTestId ? `${filterPopoverTestId}-option-${filterValueTestIdSegment(val)}` : undefined),
		[filterOptionCheckboxTestId, filterPopoverTestId],
	)
	const resolveFilterColumnButtonTestId = React.useCallback(
		(columnId: string) =>
			filterColumnButtonTestId?.(columnId) ??
			(filterPopoverTestId ? `${filterPopoverTestId}-column-${filterValueTestIdSegment(columnId)}` : undefined),
		[filterColumnButtonTestId, filterPopoverTestId],
	)
	const containerRef = React.useRef<HTMLDivElement>(null)
	const { registerClearFilters, registerFocusSearch } = useClearFilters()

	const hasColumns = Array.isArray(columns) && columns.length > 0

	const columnOperators = React.useMemo(() => {
		const ops = { ...propColumnOperators } as Record<string, FilterOption['operator'][]>
		if (Array.isArray(columns)) {
			for (const col of columns) {
				if (!ops[col.id] && col.id.startsWith('state.')) {
					ops[col.id] = ['equals', 'notEquals', 'contains', '<', '<=', '>', '>=', 'is_null', 'is_not_null']
				}
			}
		}
		return ops
	}, [propColumnOperators, columns])

	// Columns with no real operator choice (only `equals`, e.g. id/uid/Project) display as `key: value` —
	// showing `=` implies an operator the filter doesn't support.
	const isEqualsOnlyColumn = React.useCallback(
		(columnId: string) => {
			if (columnId === 'id') {
				return true
			}
			const ops = columnOperators[columnId]
			return Boolean(ops && ops.length === 1 && ops[0] === 'equals')
		},
		[columnOperators],
	)

	// Helper function for common button styles
	const getButtonClassName = React.useCallback(
		(options?: {
			isFocused?: boolean
			isSelected?: boolean
			isChecked?: boolean
			hasSelectedBorder?: boolean
			additionalClasses?: string
		}) => {
			const {
				isFocused = false,
				isSelected = false,
				isChecked = false,
				hasSelectedBorder = false,
				additionalClasses = '',
			} = options || {}

			return cn(
				'w-full text-left px-4 text-control flex items-center justify-between group transition-colors h-auto rounded-none',
				'hover:bg-accent/50',
				isFocused && 'bg-accent/50',
				isSelected && (hasSelectedBorder ? 'bg-muted/50 border-l-2 border-ring' : 'bg-muted/50'),
				isChecked && 'bg-sidebar',
				additionalClasses,
			)
		},
		[],
	)

	const [isOpen, setIsOpen] = React.useState(false)
	const [view, setView] = React.useState<'columns' | 'values' | 'suggestions' | 'operators'>('columns')
	const [selectedColumn, setSelectedColumn] = React.useState<string | null>(null)
	const [searchInput, setSearchInput] = React.useState(value || '')
	const [focusedIndex, setFocusedIndex] = React.useState<number>(-1)
	const [inputOffset, setInputOffset] = React.useState(0)
	const [clickedBadgeOffset, setClickedBadgeOffset] = React.useState<number | null>(null)
	const [rangeInputValue, setRangeInputValue] = React.useState('')
	const [customInputError, setCustomInputError] = React.useState<string | null>(null)
	const [_keyboardMode, setKeyboardMode] = React.useState<boolean>(false)
	const inputRef = React.useRef<HTMLInputElement>(null)
	const customInputRef = React.useRef<HTMLInputElement>(null)
	const [selectedOperator, setSelectedOperator] = React.useState<FilterOption['operator'] | null>(null)

	const [editingOperator, setEditingOperator] = React.useState<{
		column: string
		index: number
	} | null>(null)

	const [showCustomTimeModal, setShowCustomTimeModal] = React.useState(false)
	const [showCustomInputModal, setShowCustomInputModal] = React.useState(false)
	const [customTimeValue, setCustomTimeValue] = React.useState<number | ''>('')
	const isCustomTimeValid = typeof customTimeValue === 'number' && customTimeValue > 0
	const [customTimeUnit, setCustomTimeUnit] = React.useState<'minutes' | 'hours' | 'days'>('minutes')
	const [selectedFilterValue, setSelectedFilterValue] = React.useState<{
		column: string
		value: string
	} | null>(null)
	const [lastColumnId, setLastColumnId] = React.useState<string | null>(null)
	const [lastOperator, setLastOperator] = React.useState<FilterOption['operator'] | null>(null)
	const TIME_UNIT_OPTIONS = [
		{ value: 'minutes', label: 'Minutes' },
		{ value: 'hours', label: 'Hours' },
		{ value: 'days', label: 'Days' },
	]
	// Only caller-marked columns (e.g. timestamp/date fields) render a text box; metadata/state use the
	// suggestion list.
	const customInputColumnsSet = React.useMemo(
		() => (customInputColumns ? new Set(customInputColumns) : new Set<string>()),
		[customInputColumns],
	)
	const freeTextColumnsSet = React.useMemo(() => new Set(freeTextColumns), [freeTextColumns])
	const standaloneColumnsSet = React.useMemo(() => new Set(standaloneColumns), [standaloneColumns])

	// Server-backed value suggestions: fetch the open column's values lazily (page-by-page) with server
	// search. Debounce so typing doesn't fire a request per keystroke.
	const debouncedSearch = useDebouncedValue(searchInput, 300)
	const serverColumnEligible = React.useMemo(() => {
		if (!columnValueSource || !selectedColumn) {
			return false
		}
		if (!columnValueSource.isColumnSupported(selectedColumn)) {
			return false
		}
		const col = columns.find(c => c.id === selectedColumn)
		// Timestamp / boolean / custom-input columns are handled client-side, not via the server.
		if (col?.isTimestamp || col?.isRadio) {
			return false
		}
		if (col?.dataType && getTypeFromClickhouseToTS(col.dataType) === 'boolean') {
			return false
		}
		if (customInputColumnsSet.has(selectedColumn)) {
			return false
		}
		return true
	}, [columnValueSource, selectedColumn, columns, customInputColumnsSet])

	const serverPerPage = columnValueSource?.perPage ?? 1000
	const columnValuesQuery = useInfiniteQuery({
		queryKey: [...(columnValueSource?.queryKey ?? []), selectedColumn ?? '', debouncedSearch],
		enabled: Boolean(columnValueSource) && serverColumnEligible && isOpen && view === 'values',
		initialPageParam: 1,
		queryFn: async ({ pageParam, signal }): Promise<ColumnValueSourcePage> => {
			if (!columnValueSource || !selectedColumn) {
				return { values: [], count: 0 }
			}
			return columnValueSource.fetch({
				columnId: selectedColumn,
				search: debouncedSearch,
				page: pageParam,
				perPage: serverPerPage,
				signal,
			})
		},
		getNextPageParam: (lastPage, allPages) => {
			const loaded = allPages.reduce((sum, page) => sum + page.values.length, 0)
			return loaded < lastPage.count ? allPages.length + 1 : undefined
		},
		staleTime: 60_000,
	})

	// serverColumnEligible already implies columnValueSource is present.
	const serverColumnActive = serverColumnEligible
	// Flattened, de-duplicated distinct values across all loaded pages.
	const serverValues = React.useMemo(() => {
		if (!serverColumnActive) {
			return [] as string[]
		}
		const pages = columnValuesQuery.data?.pages ?? []
		const seen = new Set<string>()
		const out: string[] = []
		for (const page of pages) {
			for (const value of page.values) {
				const str = String(value)
				if (str.trim() === '' || seen.has(str)) {
					continue
				}
				seen.add(str)
				out.push(str)
			}
		}
		return out
	}, [serverColumnActive, columnValuesQuery.data])
	const serverTotalCount = columnValuesQuery.data?.pages?.[0]?.count ?? 0
	const serverValuesLoading = serverColumnActive && columnValuesQuery.isPending

	// Cross-column server suggestions: when the user types a value directly (no column picked), search
	// every server-backed column for matching values so the suggestion list isn't limited to the
	// client-side cache (bulk filter-options + current page), which misses most of a large fleet.
	const suggestionServerColumnIds = React.useMemo(() => {
		if (!columnValueSource) {
			return [] as string[]
		}
		return columns
			.filter(col => {
				if (!columnValueSource.isColumnSupported(col.id)) {
					return false
				}
				// Timestamp / boolean / custom-input / standalone columns keep their client-side handling.
				if (col.isTimestamp || col.isRadio) {
					return false
				}
				if (col.dataType && getTypeFromClickhouseToTS(col.dataType) === 'boolean') {
					return false
				}
				return !customInputColumnsSet.has(col.id) && !standaloneColumnsSet.has(col.id)
			})
			.map(col => col.id)
	}, [columnValueSource, columns, customInputColumnsSet, standaloneColumnsSet])

	// Active as soon as the user types (live input) — not the debounced term — so server-backed columns
	// are suppressed for the whole debounce+fetch window instead of flashing cached values first.
	const suggestionServerActive =
		Boolean(columnValueSource) && isOpen && view === 'suggestions' && searchInput.trim() !== ''
	const suggestionQueriesEnabled = suggestionServerActive && debouncedSearch.trim() !== ''
	const suggestionQueries = useQueries({
		queries: suggestionServerColumnIds.map(columnId => ({
			queryKey: [...(columnValueSource?.queryKey ?? []), 'suggest', columnId, debouncedSearch],
			enabled: suggestionQueriesEnabled,
			queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<ColumnValueSourcePage> => {
				if (!columnValueSource) {
					return { values: [], count: 0 }
				}
				return columnValueSource.fetch({
					columnId,
					search: debouncedSearch,
					page: 1,
					perPage: SUGGESTION_VALUES_PER_COLUMN,
					signal,
				})
			},
			staleTime: 60_000,
		})),
	})
	// Pending while the debounce hasn't caught up with the input OR any column request is in flight.
	const suggestionsFetching =
		suggestionServerActive &&
		(searchInput.trim() !== debouncedSearch.trim() || suggestionQueries.some(query => query.isFetching))
	// Column id → server-matched values. While ANY column is still fetching, every server-backed column
	// maps to [] so results land in one update — otherwise columns with a client-side cache (e.g. uid)
	// flash first and the rest pop in per response. After settling, only successful queries contribute;
	// failed columns fall back to the client-derived values in getSuggestions.
	const serverSuggestionValues = React.useMemo(() => {
		if (!suggestionServerActive) {
			return null
		}
		const map = new Map<string, string[]>()
		for (const [idx, query] of suggestionQueries.entries()) {
			const columnId = suggestionServerColumnIds[idx]
			if (!columnId) {
				continue
			}
			if (suggestionsFetching) {
				map.set(columnId, [])
				continue
			}
			if (query.data === undefined) {
				continue
			}
			const seen = new Set<string>()
			const values: string[] = []
			for (const value of query.data.values) {
				const str = String(value)
				if (str.trim() === '' || seen.has(str)) {
					continue
				}
				seen.add(str)
				values.push(str)
			}
			map.set(columnId, values)
		}
		return map
	}, [suggestionServerActive, suggestionQueries, suggestionServerColumnIds, suggestionsFetching])

	// Persist filters in URL
	useUrlFilters(filters, onFiltersChange, insideModal)

	// Sync input value from prop
	React.useEffect(() => {
		setSearchInput(value)
	}, [value])

	// Focus the custom input field when values view is active for a custom input column
	React.useEffect(() => {
		if (isOpen && view === 'values' && selectedColumn && customInputColumnsSet.has(selectedColumn)) {
			const timer = setTimeout(() => {
				customInputRef.current?.focus()
			}, 50)
			return () => clearTimeout(timer)
		}
	}, [isOpen, view, selectedColumn, customInputColumnsSet])

	// biome-ignore lint : /suspicious/noArrayIndexKey: React.useLayoutEffect for alignment
	React.useLayoutEffect(() => {
		const updateOffset = () => {
			if (isOpen && (view === 'columns' || view === 'values') && inputRef.current && containerRef.current) {
				const containerRect = containerRef.current.getBoundingClientRect()
				const inputRect = inputRef.current.getBoundingClientRect()
				const offset = inputRect.left - containerRect.left
				setInputOffset(offset)
			}
		}

		updateOffset()

		// Listen for window resize to keep alignment
		addEventListener('resize', updateOffset)

		return () => {
			removeEventListener('resize', updateOffset)
		}
	}, [isOpen, view, filters, selectedColumn])

	React.useLayoutEffect(() => {
		if (_keyboardMode && focusedIndex >= 0 && isOpen) {
			const dropdownContainers = document.querySelectorAll('.scrollable')
			let dropdownContainer: Element | null = null

			// Find the visible container
			for (const container of Array.from(dropdownContainers)) {
				if (container.clientHeight > 0) {
					dropdownContainer = container
					break
				}
			}

			if (!dropdownContainer) {
				dropdownContainer = document.querySelector('.scrollable')
			}

			// The values list is virtualized — VirtualizedValueList keeps the focused row in view itself.
			if (view === 'values') {
				return
			}

			const allButtons = dropdownContainer?.querySelectorAll('button[type="button"]')
			if (allButtons?.[focusedIndex]) {
				;(allButtons[focusedIndex] as HTMLElement).scrollIntoView({
					behavior: 'smooth',
					block: 'nearest',
				})
			}
		}
	}, [focusedIndex, _keyboardMode, isOpen, view])

	const handleOperatorChange = (columnId: string, newOperator: FilterOption['operator']) => {
		if (onFiltersChange) {
			const isValueless = newOperator === 'is_null' || newOperator === 'is_not_null'
			let valuelessHandled = false

			const existingColumnFilter = filters.find(f => f.column === columnId)
			let updatedFilters: FilterOption[]

			if (existingColumnFilter) {
				updatedFilters = filters.reduce<FilterOption[]>((acc, filter) => {
					if (filter.column === columnId) {
						if (isValueless) {
							// For valueless operators, keep only the first filter for this column and clear its value
							if (!valuelessHandled) {
								acc.push({
									...filter,
									operator: newOperator,
									value: '',
									isNullOperator: true,
								})
								valuelessHandled = true
							}
						} else {
							// For operators requiring values, clear values only if the previous operator was valueless
							const wasValueless = filter.operator === 'is_null' || filter.operator === 'is_not_null'
							acc.push({
								...filter,
								operator: newOperator,
								value: wasValueless ? '' : filter.value,
								isNullOperator: false,
							})
						}
					} else {
						acc.push(filter)
					}
					return acc
				}, [])
			} else if (isValueless) {
				// ADD NEW filter for valueless operator if it doesn't exist
				updatedFilters = [
					...filters,
					{
						column: columnId,
						operator: newOperator,
						value: '',
						isNullOperator: true,
					},
				]
			} else {
				// For operators requiring values when no filter exists, just return existing (will be handled by values view)
				updatedFilters = filters
			}

			onFiltersChange(updatedFilters)
		}
		setEditingOperator(null)
		if (view === 'operators') {
			setLastOperator(newOperator)
		}

		// Changing the operator on an existing chip that already has a value only swaps the operator —
		// reopening the values menu there is unwanted. Fall through only when the previous operator was
		// valueless (value cleared), so the new operator still needs a value picked.
		const isChipEdit = editingOperator !== null
		const hasExistingValue = filters.some(f => f.column === columnId && f.value !== '' && !f.isNullOperator)
		if (isChipEdit && hasExistingValue && newOperator !== 'is_null' && newOperator !== 'is_not_null') {
			return
		}

		// Automatically open the values menu if the new operator requires values
		if (newOperator !== 'is_null' && newOperator !== 'is_not_null') {
			setSelectedOperator(newOperator)
			setSearchInput('')
			setSelectedColumn(columnId)
			setView('values')
			setIsOpen(true)
			setFocusedIndex(0)
			if (customInputColumnsSet.has(columnId)) {
				setShowCustomInputModal(true)
			}
		} else {
			// For valueless operators, clear search and go back to columns view for the NEXT filter
			setSelectedOperator(null)
			setSearchInput('')
			setView('columns')
			setSelectedColumn(null)
			setIsOpen(false)
			requestAnimationFrame(() => {
				inputRef.current?.focus()
			})
		}
	}

	const getColumnValues = React.useCallback(
		(columnId: string) => {
			// Check if this is a timestamp column
			const columnConfig = columns.find(col => col.id === columnId)

			if (columnConfig?.isTimestamp) {
				// Return time range options instead of actual values
				return TIME_RANGE_OPTIONS.map(option => option.label)
			}

			// Boolean columns (incl. Nullable(Bool)) have a fixed domain — always offer true/false.
			// filter-options only returns values present on devices, so the dropdown would otherwise be
			// empty ("No values found") whenever no device has reported the field yet.
			if (columnConfig?.dataType && getTypeFromClickhouseToTS(columnConfig.dataType) === 'boolean') {
				return ['true', 'false']
			}

			// Server-provided suggestions for this column take precedence; empty/missing falls back to data
			// so we never show fewer suggestions than the data-derived behaviour.
			const serverValues = columnValueOptions?.[columnId]
			if (serverValues && serverValues.length > 0) {
				// Drop empty/whitespace-only values (e.g. a device with a blank state field), matching the
				// data-derived branch below — an empty option renders as a blank row and would otherwise be
				// selectable, sending `""` in the filter. Use is_null/is_not_null to target missing values.
				// Formatting must happen exactly once, here — downstream renderers display these values
				// as-is and would double-format (e.g. re-parsing "5 min" as a number yields garbage).
				const formatted = serverValues
					.map(v => String(v))
					.filter(v => v.trim() !== '')
					.map(v => (columnConfig && typeof columnConfig.formatValue === 'function' ? columnConfig.formatValue(v) : v))
				return [...new Set(formatted)].sort()
			}

			const values = new Set<string>()
			for (const item of data as T[]) {
				// Handle nested properties
				let val = item?.[columnId]

				// If column contains dot notation (e.g., 'state.battery'), traverse
				if (columnId.includes('.')) {
					const parts = columnId.split('.')
					val = parts.reduce(
						(obj: unknown, key) =>
							typeof obj === 'object' && obj !== null ? (obj as Record<string, unknown>)[key] : undefined,
						item,
					)
				}

				// Handle object values - extract meaningful data
				if (val !== null && val !== undefined && val !== '') {
					let formattedValue = val

					if (Array.isArray(val)) {
						for (const arrayItem of val) {
							if (arrayItem !== null && arrayItem !== undefined && arrayItem !== '') {
								const itemStr = String(arrayItem)
								if (itemStr.trim()) {
									values.add(itemStr)
								}
							}
						}
						continue
					}

					// If it's an object, try to extract a meaningful string
					if (typeof val === 'object' && val !== null) {
						// For action objects, get the type
						if (columnId === 'action' && typeof val === 'object' && val !== null && 'type' in val) {
							formattedValue = (val as { type: string }).type
							values.add(String(formattedValue))
						}
						// Skip other complex objects for filter values
						continue
					}

					if (columnConfig && typeof columnConfig.formatValue === 'function') {
						formattedValue = columnConfig.formatValue(val)
					}
					values.add(String(formattedValue))
				}
			}
			return Array.from(values).sort()
		},
		[data, columns, columnValueOptions],
	)

	// Get suggestions based on search input
	const getSuggestions = React.useCallback(
		(value?: string) => {
			const searchValue = value ?? searchInput
			if (!searchValue) {
				return []
			}

			const suggestions: Array<{
				column: string
				value: string
				columnLabel: string
				isColumnMatch?: boolean
			}> = []
			const matchingColumns = columns.filter(col => col.label.toLowerCase().includes(searchValue.toLowerCase()))
			// Add column matches first (with special flag)
			for (const column of matchingColumns) {
				suggestions.push({
					column: column.id,
					value: '', // Empty value for column-only matches
					columnLabel: column.label,
					isColumnMatch: true,
				})
			}
			// Free-text columns: offer the raw typed value as a selectable suggestion so a value missing
			// from the (client-side) cache is still applyable + forwarded to the backend. When the column
			// is server-backed, an empty entry ([]) means "in flight or settled with zero matches" — hide
			// the raw entry then: it would leak through the atomic paint, and a zero-match term applied as
			// a contains-chip returns no devices anyway. Query errors leave no entry, so the raw fallback
			// stays offered.
			const freeTextSuggestions: typeof suggestions = []
			for (const column of columns) {
				if (freeTextColumnsSet.has(column.id)) {
					const serverMatches = serverSuggestionValues?.get(column.id)
					if (serverMatches !== undefined && serverMatches.length === 0) {
						continue
					}
					freeTextSuggestions.push({
						column: column.id,
						value: searchValue,
						columnLabel: column.label,
					})
				}
			}
			const valueSuggestions: typeof suggestions = []
			for (const column of columns) {
				// Server-matched values take precedence; columns whose query is pending/failed/unsupported fall
				// back to the client-derived cache below. Server values are ILIKE-filtered on the debounced
				// term — re-narrow by the live input so the list never shows a lagging term's matches.
				const serverMatches = serverSuggestionValues?.get(column.id)
				const matchingValues = (serverMatches ?? getColumnValues(column.id)).filter(v =>
					v.toLowerCase().includes(searchValue.toLowerCase()),
				)

				for (const val of matchingValues) {
					// Skip values already surfaced as a free-text entry above to avoid duplicates.
					if (freeTextColumnsSet.has(column.id) && val === searchValue) {
						continue
					}
					valueSuggestions.push({
						column: column.id,
						value: val,
						columnLabel: column.label,
					})
				}
			}

			// An exact value, such as a full rule name, comes before the free text entries so Enter picks it.
			const typed = searchValue.toLowerCase()
			const isExact = (suggestion: { value: string }) => suggestion.value.toLowerCase() === typed
			return [
				...suggestions,
				...valueSuggestions.filter(isExact),
				...freeTextSuggestions,
				...valueSuggestions.filter(suggestion => !isExact(suggestion)),
			]
		},
		[searchInput, columns, getColumnValues, freeTextColumnsSet, serverSuggestionValues],
	)
	const dropdownRef = React.useRef<HTMLDivElement>(null)

	React.useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			const target = event.target as Node
			const element = target as Element

			// Check if click is on Select or DropdownMenu portals/components
			const isSelectContent = element.closest('[data-slot="select-content"]') !== null
			const isDropdownMenuContent = element.closest('[data-slot="dropdown-menu-content"]') !== null
			const isSelectTrigger = element.closest('[data-slot="select-trigger"]') !== null
			const isDropdownTrigger = element.closest('[data-slot="dropdown-menu-trigger"]') !== null
			const isSelectPortal = element.closest('[data-radix-portal]') !== null && isSelectContent
			const isDropdownPortal = element.closest('[data-radix-portal]') !== null && isDropdownMenuContent

			// Check if click is outside both the container AND the dropdown
			const isOutsideContainer = containerRef.current && !containerRef.current.contains(target)
			const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target)

			// Don't close if clicking on Select or DropdownMenu components
			if (
				isSelectContent ||
				isDropdownMenuContent ||
				isSelectTrigger ||
				isDropdownTrigger ||
				isSelectPortal ||
				isDropdownPortal
			) {
				return
			}

			if (isOutsideContainer && isOutsideDropdown) {
				if (!selectedColumn && view === 'suggestions') {
					setSearchInput('')
					onChange('')
				}

				setIsOpen(false)
				setView('columns')
			}
		}
		// Capture phase: an ancestor (e.g. bottom bar wrapper) stopPropagation's bubble-phase mousedown
		// before it reaches document. Capture fires first, so outside-click detection still works.
		document.addEventListener('mousedown', handleClickOutside, true)
		return () => document.removeEventListener('mousedown', handleClickOutside, true)
	}, [selectedColumn, view, onChange])

	// Clean up intermediate states and remove empty placeholder filters
	React.useEffect(() => {
		if (!isOpen || !selectedColumn) {
			const cleanedFilters = filters.filter(f => !(f.value === '' && !f.isNullOperator))
			if (onFiltersChange && cleanedFilters.length !== filters.length) {
				onFiltersChange(cleanedFilters)
			}
		}

		if (!isOpen) {
			setSelectedColumn(null)
			setSelectedOperator(null)
			setSelectedFilterValue(null)
			setSearchInput('')
			setShowCustomTimeModal(false)
			setShowCustomInputModal(false)
			setRangeInputValue('')
			setCustomTimeValue('')
			setCustomTimeUnit('minutes')
		}
	}, [isOpen, selectedColumn, filters, onFiltersChange])

	const handleInputClick = () => {
		// Don't handle input click when custom time modal is open
		if (showCustomTimeModal || showCustomInputModal) {
			return
		}
		if (selectedFilterValue) {
			return
		}
		// setSelectedFilterValue(null)
		if (hasColumns && !disabled) {
			// If dropdown is already open, don't auto-select ID again
			if (isOpen) {
				inputRef.current?.focus()
				return
			}

			setIsOpen(true)
			// Set first item as focused so it appears ready for selection
			setFocusedIndex(0)
			// Reset badge offset so dropdown appears at input position
			setClickedBadgeOffset(null)
			// Focus immediately
			inputRef.current?.focus()
		}
	}

	// Enhanced keyboard navigation
	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (showCustomTimeModal) {
			return
		}
		// Handle Space key
		if (e.key === ' ' && !searchInput) {
			e.preventDefault()
			// If in values view, go DIRECTLY to columns (skip operators)
			if (isOpen && view === 'values') {
				setView('columns')
				setSelectedColumn(null)
				setSelectedOperator(null)
				setSelectedFilterValue(null)
				setShowCustomTimeModal(false)
				setShowCustomInputModal(false)
				setFocusedIndex(0)
				setKeyboardMode(true)
			} else if (isOpen && view === 'suggestions') {
				// For suggestions, use handleBack as before
				handleBack()
				setSelectedFilterValue(null)
				setKeyboardMode(true)
			} else {
				// Open columns menu
				setIsOpen(true)
				setView('columns')
				setSelectedColumn(null)
				setSelectedOperator(null)
				setSelectedFilterValue(null)
				setFocusedIndex(0)
				setKeyboardMode(true)
			}
		}

		// Handle Backspace or Delete to remove filters
		if ((e.key === 'Backspace' || e.key === 'Delete') && !searchInput) {
			e.preventDefault()
			if (selectedFilterValue) {
				// First priority: Remove selected filter value if one is selected
				if (onFiltersChange) {
					const newFilters = filters.filter(
						f => !(f.column === selectedFilterValue.column && f.value === selectedFilterValue.value),
					)
					onFiltersChange(newFilters)
				}
				setSelectedFilterValue(null)
				return // Exit early
			}

			// If we are actively sitting in a selected column, but it has no filters left, backspace aborts the column
			if (selectedColumn && !filters.some(f => f.column === selectedColumn)) {
				setView('columns')
				setSelectedColumn(null)
				return
			}

			// If there are no filters globally, do nothing
			if (filters.length === 0) {
				return
			}

			// Default backspace behavior: remove the last filter of the selected column (if any), else the last global column
			const columnsWithFilters = Object.keys(
				filters.reduce<Record<string, string[]>>((acc, f) => {
					if (!acc[f.column]) {
						acc[f.column] = []
					}
					acc[f.column].push(f.value)
					return acc
				}, {}),
			)

			if (columnsWithFilters.length === 0) {
				return
			}

			const targetColumn =
				selectedColumn && columnsWithFilters.includes(selectedColumn)
					? selectedColumn
					: columnsWithFilters[columnsWithFilters.length - 1]

			if (onFiltersChange) {
				const columnFilters = filters.filter(f => f.column === targetColumn)
				if (columnFilters.length > 0) {
					const lastValueFilter = columnFilters[columnFilters.length - 1]

					if (lastValueFilter.value === '') {
						// It is already functionally empty, simply rip it out
						const finalFilters = filters.filter(f => f !== lastValueFilter)
						onFiltersChange(finalFilters)
						if (targetColumn === selectedColumn) {
							setSelectedColumn(null)
							setView('columns')
						}
					} else {
						const remainingFiltersForColumn = columnFilters.filter(f => f !== lastValueFilter)
						let finalFilters = filters.filter(f => f !== lastValueFilter)

						// If we just erased the LAST valid filter for this column, insert an empty trap to preserve global visual column order!
						if (remainingFiltersForColumn.length === 0) {
							finalFilters = filters.map(f => (f === lastValueFilter ? { ...f, value: '' } : f))
						}
						onFiltersChange(finalFilters)
					}
				}
			}
		}

		// Handle Arrow Left - Go back to columns menu
		if (e.key === 'ArrowLeft') {
			e.preventDefault()

			if (isOpen && (view === 'values' || view === 'suggestions')) {
				handleBack()
				setKeyboardMode(true)
			}
		}

		// Handle Arrow Down
		if (e.key === 'ArrowDown') {
			e.preventDefault()
			setKeyboardMode(true)

			if (!isOpen) {
				setIsOpen(true)
				setView('columns')
				setFocusedIndex(0)
				return
			}

			const maxIndex =
				view === 'columns'
					? filteredColumns.length - 1
					: view === 'suggestions'
						? suggestions.length - 1
						: view === 'operators' && selectedColumn
							? (columnOperators[selectedColumn]?.length || 0) - 1
							: currentValues.length - 1

			setFocusedIndex(prev => (prev < maxIndex ? prev + 1 : prev))
		}

		// Handle Arrow Up
		if (e.key === 'ArrowUp') {
			e.preventDefault()
			setKeyboardMode(true)
			setFocusedIndex(prev => (prev > 0 ? prev - 1 : 0))
		}

		// Handle Enter to select/toggle
		if (e.key === 'Enter' && isOpen) {
			e.preventDefault()

			if (view === 'columns' && focusedIndex >= 0 && focusedIndex < filteredColumns.length) {
				const selectedCol = filteredColumns[focusedIndex]
				if (selectedCol) {
					handleColumnClick(selectedCol.id)
				}
			} else if (view === 'suggestions' && focusedIndex >= 0 && focusedIndex < suggestions.length) {
				const suggestion = suggestions[focusedIndex]
				if (suggestion) {
					handleSuggestionClick(suggestion)
				}
			} else if (view === 'operators' && focusedIndex >= 0 && selectedColumn) {
				const operators = columnOperators[selectedColumn] || []
				const selectedOp = operators[focusedIndex]
				if (selectedOp) {
					handleOperatorChange(selectedColumn, selectedOp)
				}
			} else if (view === 'values') {
				if (focusedIndex >= 0 && focusedIndex < currentValues.length) {
					const value = currentValues[focusedIndex]
					if (value) {
						handleValueClick(value)
					}
				} else if (searchInput.trim() && !typedValueError) {
					handleValueClick(searchInput.trim())
				}
			}
		}

		// Handle Escape to close or go back
		if (e.key === 'Escape') {
			e.preventDefault()
			if (view === 'values' || view === 'suggestions') {
				handleBack()
			} else {
				setIsOpen(false)
				setFocusedIndex(0)
				setKeyboardMode(false)
			}
		}
	}

	const handleMouseEnter = (index?: number) => {
		setKeyboardMode(false)
		if (typeof index === 'number') {
			setFocusedIndex(index)
		}
	}

	const handleMouseLeave = () => {
		setFocusedIndex(-1)
	}
	const handleColumnClick = (columnId: string) => {
		// Standalone columns apply on this single click — no operator/value step, no chip value shown.
		if (standaloneColumnsSet.has(columnId)) {
			if (onFiltersChange) {
				const others = filters.filter(f => f.column !== columnId)
				const standaloneValue = getColumnValues(columnId)[0] ?? getColumnLabel(columnId)
				onFiltersChange([
					...others,
					{ column: columnId, operator: 'equals', value: standaloneValue, isNullOperator: false },
				])
			}
			setIsOpen(false)
			setView('columns')
			setSelectedColumn(null)
			setSearchInput('')
			setFocusedIndex(-1)
			requestAnimationFrame(() => inputRef.current?.focus())
			return
		}

		setSearchInput('')
		setLastColumnId(columnId)
		setSelectedColumn(columnId)
		setSelectedOperator(null)
		setShowCustomTimeModal(false)
		setFocusedIndex(0)
		setKeyboardMode(true)

		const isCustomInput = customInputColumnsSet.has(columnId)

		// Check if this column should skip operator selection
		const shouldSkipOperators =
			skipOperatorSelection?.includes(columnId) || // Skip if in skip list
			!columnOperators[columnId] || // Skip if no operators defined
			columnOperators[columnId].length <= 1 || // Skip if only one operator
			columnId === 'id' // Always skip for ID column

		if (shouldSkipOperators) {
			// Go directly to values
			setView('values')
			// Set flag if it's a custom input column
			if (isCustomInput) {
				setShowCustomInputModal(true)
			}
		} else {
			// Show operator selection
			setView('operators')
			setShowCustomInputModal(false)
		}

		if (!isCustomInput) {
			// Focus input after column selection only if not custom input
			requestAnimationFrame(() => {
				inputRef.current?.focus()
			})
		}
	}

	const handleSuggestionClick = (suggestion: {
		column: string
		value: string
		isColumnMatch?: boolean
	}) => {
		if (suggestion.isColumnMatch) {
			handleColumnClick(suggestion.column)
			setSearchInput('')
			return
		}
		setSelectedColumn(null)
		setView('columns')
		setSearchInput('')

		if (onFiltersChange) {
			const columnConfig = columns.find(col => col.id === suggestion.column)
			const isTimestampColumn = columnConfig?.isTimestamp
			const isRadioColumn = columnConfig?.isRadio

			const existingFilters = filters.filter(f => f.column === suggestion.column)
			const otherFilters = filters.filter(f => f.column !== suggestion.column)

			// Determine the default operator for this column
			let defaultOperator = columnOperators[suggestion.column]?.[0] || 'equals'
			let filterValue = suggestion.value

			// For timestamp columns, handle default operator logic
			if (isTimestampColumn) {
				const allowedOps = columnOperators[suggestion.column] || []
				// If column has specific operators and they don't include timeRange, use the first one or equals
				// This supports using isTimestamp for "Duration" columns where we want comparison operators
				if (allowedOps.length > 0 && !allowedOps.includes('timeRange')) {
					defaultOperator = allowedOps[0]
				} else {
					defaultOperator = 'timeRange'
				}

				const timeOption = TIME_RANGE_OPTIONS.find(opt => opt.label === suggestion.value)
				filterValue = timeOption?.value || suggestion.value

				// For timestamp columns, replace existing filter
				onFiltersChange([
					...otherFilters,
					{
						column: suggestion.column,
						operator: defaultOperator,
						value: filterValue,
						isNullOperator: false,
					},
				])
				// Focus input after selection
				requestAnimationFrame(() => {
					inputRef.current?.focus()
				})
				return true
			}

			if (isRadioColumn) {
				// For radio columns, replace existing filter
				onFiltersChange([
					...otherFilters,
					{
						column: suggestion.column,
						operator: defaultOperator,
						value: filterValue,
						isNullOperator: false,
					},
				])
				// Focus input after selection
				requestAnimationFrame(() => {
					inputRef.current?.focus()
				})
				return true
			}

			// Block an invalid free-typed suggestion value (e.g. non-numeric UID, over-long substring) before
			// it reaches the server. Mirrors handleValueClick; only the metadata/state value path (timestamp
			// and radio already returned above). Substring cap applies only to `contains`.
			const suggestionError =
				(validateInput
					? validateInput(suggestion.column, filterValue)
					: validateInputValue(filterValue, columnConfig?.dataType ?? '')) ??
				(validateSubstringLength && defaultOperator === 'contains' ? validateSubstringValueLength(filterValue) : null)
			if (suggestionError) {
				setCustomInputError(suggestionError)
				return
			}

			const valueAlreadyExists = existingFilters.some(f => f.value === filterValue)

			if (valueAlreadyExists) {
				onFiltersChange(filters)
			} else {
				onFiltersChange([
					...otherFilters,
					...existingFilters,
					{
						column: suggestion.column,
						operator: defaultOperator,
						value: filterValue,
						isNullOperator: false,
					},
				])
			}
		}

		// Focus input after selecting a suggestion with mouse/touchpad
		requestAnimationFrame(() => {
			inputRef.current?.focus()
		})
	}
	// Update handleValueClick function (around line 540)
	const handleValueClick = (value: string): boolean => {
		let error: string | null = null
		if (selectedColumn) {
			if (validateInput) {
				error = validateInput(selectedColumn, value)
			} else if (currentColumnDataType) {
				error = validateInputValue(value, currentColumnDataType)
			}
			error = error ?? substringLengthError(value)
			if (error) {
				setCustomInputError(error)
				return false
			}
		}
		setCustomInputError(null)

		if (selectedColumn && onFiltersChange) {
			const columnConfig = columns.find(col => col.id === selectedColumn)
			const isTimestampColumn = columnConfig?.isTimestamp

			const isCustomInputColumn = customInputColumnsSet.has(selectedColumn)

			if (isTimestampColumn && value === 'Custom') {
				setShowCustomTimeModal(true)
				return true
			}

			// Find the original index of the first filter for this column to maintain order
			const originalIndex = filters.findIndex(f => f.column === selectedColumn)
			const existingFilters = filters.filter(f => f.column === selectedColumn)

			// Get current operator for this column (from existing filters or default)
			let currentOperator =
				selectedOperator || // ← First priority!
				existingFilters[0]?.operator ||
				columnOperators[selectedColumn]?.[0] ||
				'equals'

			let filterValue = value

			// Helper function to maintain filter order
			const buildNewFilters = (columnFilters: FilterOption[]) => {
				if (originalIndex === -1) {
					// No existing filters for this column, append at end
					return [...filters, ...columnFilters]
				}
				// Insert at original position
				const before = filters.slice(0, originalIndex).filter(f => f.column !== selectedColumn)
				const after = filters.slice(originalIndex).filter(f => f.column !== selectedColumn)
				return [...before, ...columnFilters, ...after]
			}

			// For timestamp columns
			if (isTimestampColumn) {
				const allowedOps = columnOperators[selectedColumn] || []
				// If column allows other operators (like duration comparisons), respect selected or default
				// Otherwise force timeRange (standard timestamp behavior)
				if (selectedOperator) {
					currentOperator = selectedOperator
				} else if (allowedOps.length > 0 && !allowedOps.includes('timeRange')) {
					currentOperator = allowedOps[0]
				} else {
					currentOperator = 'timeRange'
				}

				const timeOption = TIME_RANGE_OPTIONS.find(opt => opt.label === value)
				filterValue = timeOption?.value || value

				const newFilters = buildNewFilters([
					{
						column: selectedColumn,
						operator: currentOperator,
						value: filterValue,
						isNullOperator: false,
					},
				])
				onFiltersChange(newFilters)
				setIsOpen(false)
				setView('columns')
				setSearchInput('')
				setSelectedColumn(null)
				setSelectedOperator(null)
				requestAnimationFrame(() => {
					inputRef.current?.focus()
				})
				return true
			}

			const isRadioColumn = columnConfig?.isRadio

			if (isRadioColumn) {
				const newFilters = buildNewFilters([
					{
						column: selectedColumn,
						operator: currentOperator,
						value: filterValue,
						isNullOperator: false,
					},
				])
				onFiltersChange(newFilters)
				setIsOpen(false)
				setView('columns')
				setSearchInput('')
				setSelectedColumn(null)
				setSelectedOperator(null)
				requestAnimationFrame(() => {
					inputRef.current?.focus()
				})
				return true
			}

			// For custom input columns (device state), allow multiple values with same operator
			if (isCustomInputColumn) {
				// Check if we are "editing" an existing value
				// If selectedFilterValue is set for this column, we should remove the old value first
				let filtersToUpdate = existingFilters

				if (selectedFilterValue?.column === selectedColumn && selectedFilterValue.value) {
					// Remove the old value we are editing
					filtersToUpdate = existingFilters.filter(f => f.value !== selectedFilterValue.value)
				}

				// Check if the NEW value already exists in the (potentially updated) list
				// This prevents adding duplicates if the user edits "A" to become "B" but "B" already exists
				const valueLimitReached = filtersToUpdate.some(f => f.value === filterValue)

				if (valueLimitReached) {
					// If the value exists, we don't add it again.
					// If we were editing, we effectively removed the old value and "merged" into the existing identical value.
					const newFilters = buildNewFilters(filtersToUpdate)
					onFiltersChange(newFilters)
				} else {
					// Add new filter with current operator
					const updatedFilters = [
						...filtersToUpdate,
						{
							column: selectedColumn,
							operator: currentOperator,
							value: filterValue,
							isNullOperator: false,
						},
					]
					const newFilters = buildNewFilters(updatedFilters)
					onFiltersChange(newFilters)
				}
				setSearchInput('')
				// Clear selection after edit
				setSelectedFilterValue(null)
				setRangeInputValue('')

				requestAnimationFrame(() => {
					inputRef.current?.focus()
				})
				return true
			}

			// For regular columns (existing behavior)
			const valueAlreadySelected = existingFilters.some(f => f.value === filterValue)

			const updatedColumnFilters = valueAlreadySelected
				? existingFilters.filter(f => f.value !== filterValue)
				: [
						...existingFilters,
						{
							column: selectedColumn,
							operator: currentOperator,
							value: filterValue,
							isNullOperator: false,
						} as const,
					]

			// If the user unchecks the final active value from the values menu, insert a placeholder to prevent UI jumping
			if (updatedColumnFilters.length === 0) {
				updatedColumnFilters.push({
					column: selectedColumn,
					operator: currentOperator,
					value: '',
					isNullOperator: false,
				})
			}

			const newFilters = buildNewFilters(updatedColumnFilters)
			onFiltersChange(newFilters)
			setSearchInput('')
			setTimeout(() => {
				if (inputRef.current) {
					inputRef.current.focus()
				}
			}, 0)
		}
		return true
	}
	const handleApplyCustomTime = () => {
		if (!isCustomTimeValid || !selectedColumn) {
			return
		}

		// Convert to milliseconds
		const oneMinInMs = 60 * 1000
		const oneHrInMs = 60 * oneMinInMs
		const oneDayInMs = 24 * oneHrInMs

		let ms = 0
		switch (customTimeUnit) {
			case 'minutes':
				ms = customTimeValue * oneMinInMs
				break
			case 'hours':
				ms = customTimeValue * oneHrInMs
				break
			case 'days':
				ms = customTimeValue * oneDayInMs
				break
		}

		const customValue = `${ms}ms`

		const columnConfig = columns.find(col => col.id === selectedColumn)
		let operator: FilterOption['operator'] = 'timeRange'

		if (columnConfig?.isTimestamp) {
			const allowedOps = columnOperators[selectedColumn] || []
			if (allowedOps.length > 0 && !allowedOps.includes('timeRange')) {
				operator = selectedOperator || allowedOps[0]
			}
		}

		let filtersToKeep = filters

		// If editing an existing value, remove it first
		if (selectedFilterValue?.column === selectedColumn && selectedFilterValue.value) {
			filtersToKeep = filters.filter(f => !(f.column === selectedColumn && f.value === selectedFilterValue.value))
		} else if (operator === 'timeRange') {
			// If we are ADDING a new timeRange filter (not editing), and it's a standard relative time range,
			// typically we only want one "Last X" filter per column.
			filtersToKeep = filters.filter(f => f.column !== selectedColumn)
		} else {
			// For comparison operators (duration), we allow multiple, so we just append (keep all existing)
			filtersToKeep = filters
		}

		if (onFiltersChange) {
			onFiltersChange([...filtersToKeep, { column: selectedColumn, operator: operator, value: customValue }])
		}

		// Reset and close modal
		setShowCustomTimeModal(false)
		setCustomTimeValue('')
		setCustomTimeUnit('minutes')

		setIsOpen(false)
		setView('columns')
		setSelectedColumn(null)
		setSelectedOperator(null)
		requestAnimationFrame(() => {
			inputRef.current?.focus()
		})
	}
	// Select all values for current column
	const handleSelectAll = () => {
		if (selectedColumn && onFiltersChange) {
			// Server columns: apply at most the first SELECT_ALL_MAX loaded values (scroll loads more).
			const allValues = serverColumnActive ? serverValues.slice(0, SELECT_ALL_MAX) : currentValues
			// Nothing loaded yet (server skeleton): bail so we don't wipe existing selections with an empty list.
			if (allValues.length === 0) {
				return
			}
			const otherFilters = filters.filter(f => f.column !== selectedColumn)

			// Determine the default operator for this column
			const defaultOperator = columnOperators[selectedColumn]?.[0] || 'equals'

			const newFilters = allValues.map(value => ({
				column: selectedColumn,
				operator: defaultOperator,
				value,
			}))
			onFiltersChange([...otherFilters, ...newFilters])
		}
	}
	// Clear all values for current column
	const handleClearAll = () => {
		if (selectedColumn && onFiltersChange) {
			const otherFilters = filters.filter(f => f.column !== selectedColumn)
			onFiltersChange(otherFilters)
		}
	}

	const handleClear = () => {
		onChange('')
		setSearchInput('')
		if (onFiltersChange) {
			onFiltersChange([])
		}
		// Reset all filter-related state when clear is clicked
		setSelectedColumn(null)
		setSelectedOperator(null)
		setSelectedFilterValue(null)
		setIsOpen(false)
		setView('columns')
		setFocusedIndex(-1)
		setShowCustomTimeModal(false)
		setShowCustomInputModal(false)
		setRangeInputValue('')
		setClickedBadgeOffset(null)
	}
	// Automatically register the clear filters function for this search bar
	// But ONLY if we are the main search bar (not inside a modal/bottom bar)
	React.useEffect(() => {
		if (!insideModal && onFiltersChange) {
			const clearFn = () => {
				onFiltersChange([])
				// Also clear internal partial state
				setSelectedColumn(null)
				setSelectedOperator(null)
				setSelectedFilterValue(null)
				setIsOpen(false)
				setView('columns')
				setFocusedIndex(-1)
				setShowCustomTimeModal(false)
				setShowCustomInputModal(false)
				setRangeInputValue('')
				setClickedBadgeOffset(null)
				setSearchInput('')
			}
			registerClearFilters(clearFn)
			return () => registerClearFilters(null)
		}
	}, [onFiltersChange, insideModal, registerClearFilters])

	React.useEffect(() => {
		if (!insideModal) {
			const focusFn = () => inputRef.current?.focus()
			registerFocusSearch(focusFn)
			return () => registerFocusSearch(null)
		}
	}, [insideModal, registerFocusSearch])

	// Update handleBack function (around line 655)
	const handleBack = () => {
		const allSortedColumns = getSortedColumns(columns)
		const colIndex = lastColumnId ? allSortedColumns.findIndex(c => c.id === lastColumnId) : 0

		// If in values view, go back to operators (if applicable)
		if (view === 'values' && selectedColumn) {
			const shouldSkipOperators =
				skipOperatorSelection?.includes(selectedColumn) ||
				!columnOperators[selectedColumn] ||
				columnOperators[selectedColumn].length <= 1 ||
				selectedColumn === 'id'

			if (shouldSkipOperators) {
				// Go back to columns
				setView('columns')
				setSelectedColumn(null)
				setSelectedOperator(null)
				setSelectedFilterValue(null)
				setFocusedIndex(colIndex >= 0 ? colIndex : 0)
			} else {
				// Go back to operators
				setView('operators')
				setSelectedFilterValue(null)
				const allowedOperators = columnOperators[selectedColumn] || []
				const opIndex = lastOperator ? allowedOperators.indexOf(lastOperator) : 0
				setFocusedIndex(opIndex >= 0 ? opIndex : 0)
			}
		} else if (view === 'operators') {
			// From operators, go back to columns
			setView('columns')
			setSelectedColumn(null)
			setSelectedOperator(null)
			setSelectedFilterValue(null)
			setFocusedIndex(colIndex >= 0 ? colIndex : 0)
		} else {
			// From any other view, go back to columns
			setView('columns')
			setSelectedColumn(null)
			setSelectedOperator(null)
			setSelectedFilterValue(null)
			setFocusedIndex(colIndex >= 0 ? colIndex : 0)
		}

		setSearchInput('')
		setShowCustomTimeModal(false)
		setShowCustomInputModal(false)

		// Focus input after going back
		requestAnimationFrame(() => {
			inputRef.current?.focus()
		})
	}

	const handlePaste = React.useCallback(
		(e: React.ClipboardEvent<HTMLInputElement>) => {
			if (!onFiltersChange) {
				return
			}
			const text = e.clipboardData.getData('text')
			const pasteMatch = text.match(/^(.+?)\s*:\s*(.+)$/)
			if (!pasteMatch) {
				return
			}
			const labelPart = pasteMatch[1].trim()
			const valuesPart = pasteMatch[2].trim()
			const col = columns.find(c => c.label.toLowerCase() === labelPart.toLowerCase())
			if (!col) {
				return
			}
			e.preventDefault()
			let values = valuesPart
				.split(',')
				.map(v => v.trim())
				.filter(v => v.length > 0)
			if (!values.length) {
				return
			}
			if (values.length > 1000) {
				values = values.slice(0, 1000)
				filterLimitFlags.justWarnedPasteLimit = true
				toast.warning('You have pasted a large amount of data. Filters are limited to the first 1,000 items.')
			}
			const existingOtherFilters = filters.filter(f => f.column !== col.id)
			const newFilters: FilterOption[] = values.map(v => ({
				column: col.id,
				operator: 'equals' as FilterOption['operator'],
				value: v,
				isNullOperator: false,
			}))
			onFiltersChange([...existingOtherFilters, ...newFilters])
			setSearchInput('') // clear raw pasted text — chips now show applied filters
			setView('columns') // reset dropdown to column list for next interaction
			setSelectedColumn(null) // clear mid-flow column selection
			setIsOpen(false) // close dropdown — filters already applied
		},
		[onFiltersChange, columns, filters],
	)

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value
		setSearchInput(newValue)

		// If we're in values view with a selected column, stay in values view
		// This allows filtering the values while keeping the column context
		if (view === 'values' && selectedColumn) {
			setFocusedIndex(0)
			return
		}

		// If we're in operators view, stay there
		if (view === 'operators' && selectedColumn) {
			return
		}

		// If empty, reset view and clear search
		if (!newValue.trim()) {
			if (!selectedColumn) {
				setView('columns')
				setShowCustomTimeModal(false)
				setSelectedColumn(null)
				onChange(newValue)
			}
			setFocusedIndex(0)
			return
		}

		// Set view to suggestions when search text is typed
		setView('suggestions')
	}

	const toggleLogicOperator = (currentColumnId: string, nextColumnId: string) => {
		if (!allowLogicOperatorChange || !onOrGroupsChange) {
			return
		}

		// Find if these columns are already in an OR group
		let foundGroup: string[] | null = null
		let groupIndex = -1

		for (let i = 0; i < orGroups.length; i++) {
			const group = orGroups[i]
			if (group.includes(currentColumnId) && group.includes(nextColumnId)) {
				foundGroup = group
				groupIndex = i
				break
			}
		}

		if (foundGroup) {
			// Currently OR - switch to AND by removing from OR group
			const updatedGroups = orGroups
				.map((group, idx) => {
					if (idx === groupIndex) {
						// Remove both columns from this group
						return group.filter(col => col !== currentColumnId && col !== nextColumnId)
					}
					return group
				})
				.filter(group => group.length > 1) // Remove groups with less than 2 columns

			onOrGroupsChange(updatedGroups)
		} else {
			// Currently AND - switch to OR by adding to OR group
			// Check if either column is already in an OR group
			const existingGroupIndex = orGroups.findIndex(
				group => group.includes(currentColumnId) || group.includes(nextColumnId),
			)

			if (existingGroupIndex !== -1) {
				// Add to existing group
				const updatedGroups = orGroups.map((group, idx) => {
					if (idx === existingGroupIndex) {
						const newGroup = [...group]
						if (!newGroup.includes(currentColumnId)) {
							newGroup.push(currentColumnId)
						}
						if (!newGroup.includes(nextColumnId)) {
							newGroup.push(nextColumnId)
						}
						return newGroup
					}
					return group
				})
				onOrGroupsChange(updatedGroups)
			} else {
				// Create new OR group
				onOrGroupsChange([...orGroups, [currentColumnId, nextColumnId]])
			}
		}
	}

	const getLogicOperator = (currentColumnId: string, nextColumnId: string) => {
		// Check if both columns are in the same OR group
		for (const group of orGroups) {
			if (group.includes(currentColumnId) && group.includes(nextColumnId)) {
				return 'OR'
			}
		}
		return 'AND'
	}

	// `contains` values become a server-side regex capped at 64 bytes; flag over-long ones. Only when the
	// consumer opts in (device search) and only for `contains` (equals/in aren't regexes).
	const substringLengthError = React.useCallback(
		(value: string): string | null => {
			if (!validateSubstringLength || !selectedColumn) {
				return null
			}
			const effectiveOperator =
				selectedOperator ||
				filters.find(f => f.column === selectedColumn)?.operator ||
				columnOperators[selectedColumn]?.[0] ||
				'equals'
			if (effectiveOperator !== 'contains') {
				return null
			}
			return validateSubstringValueLength(value)
		},
		[validateSubstringLength, selectedColumn, selectedOperator, filters, columnOperators],
	)

	// Validate the typed value (e.g. non-numeric UID, over-long substring). Non-null = invalid: not
	// offered as an option, can't be applied, message shown.
	const typedValueError = React.useMemo(() => {
		const trimmed = searchInput.trim()
		if (!selectedColumn || !trimmed) {
			return null
		}
		// A time column only takes its listed ranges; a typed number such as -6 is no range at all.
		if (columns.find(c => c.id === selectedColumn)?.isTimestamp) {
			const lower = trimmed.toLowerCase()
			return getColumnValues(selectedColumn).some(v => v.toLowerCase().includes(lower))
				? null
				: 'Pick a time range from the list, or choose Custom'
		}
		const baseError = validateInput
			? validateInput(selectedColumn, trimmed)
			: validateInputValue(trimmed, columns.find(c => c.id === selectedColumn)?.dataType ?? '')
		return baseError ?? substringLengthError(trimmed)
	}, [selectedColumn, searchInput, columns, getColumnValues, validateInput, substringLengthError])

	const currentValuesFull = React.useMemo(() => {
		if (!selectedColumn) {
			return []
		}

		// Server already returns distinct + sorted + search-filtered values. Pin active selections on top,
		// allow a free-typed value, and narrow on the client during the debounce.
		if (serverColumnActive) {
			// Pin selected values not in the loaded page on top; lowercased Set keeps this O(n) at ~1000 selected.
			const base = [...serverValues]
			const seenLower = new Set(serverValues.map(v => v.toLowerCase()))
			for (const f of filters) {
				if (f.column !== selectedColumn || f.value === '') {
					continue
				}
				const lower = f.value.toLowerCase()
				if (!seenLower.has(lower)) {
					seenLower.add(lower)
					base.unshift(f.value)
				}
			}
			const trimmed = searchInput.trim()
			if (!trimmed) {
				return base
			}
			const trimmedLower = trimmed.toLowerCase()
			const filtered = base.filter(v => v.toLowerCase().includes(trimmedLower))
			// Only offer the free-typed value when it's valid for the column (e.g. numeric UID). Invalid
			// input just shows matches; typedValueError explains why it can't be added.
			if (!typedValueError && !filtered.some(v => v.toLowerCase() === trimmedLower)) {
				return [trimmed, ...filtered]
			}
			return filtered
		}

		const values = getColumnValues(selectedColumn)

		// Check if this is a timestamp column (values will have "Custom" first from getColumnValues)
		const columnConfig = columns.find(col => col.id === selectedColumn)
		const isTimestampColumn = columnConfig?.isTimestamp

		if (isTimestampColumn) {
			// For timestamp columns, keep Custom first, don't sort
			if (!searchInput) {
				return values
			}
			return values.filter(v => v.toLowerCase().includes(searchInput.toLowerCase()))
		}

		// Sort values naturally (without assuming any input)
		const sortedValues = [...values].sort((a, b) => {
			// Try numeric comparison first
			const numA = Number.parseFloat(a)
			const numB = Number.parseFloat(b)
			if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
				return numA - numB
			}
			// Fall back to string comparison
			return a.localeCompare(b)
		})
		// Gather currently selected values for this column in filters
		const activeSelectedValues = filters.filter(f => f.column === selectedColumn && f.value !== '').map(f => f.value)

		// Combine sorted values with active selected values, ensuring active custom selections are always present
		const baseValues = [...sortedValues]
		for (const val of activeSelectedValues) {
			if (!baseValues.some(v => v.toLowerCase() === val.toLowerCase())) {
				baseValues.unshift(val) // Prepend active selections for visibility
			}
		}

		if (!searchInput) {
			return baseValues
		}

		const trimmedSearch = searchInput.trim()
		const filtered = baseValues.filter(v => v.toLowerCase().includes(trimmedSearch.toLowerCase()))
		if (
			trimmedSearch &&
			!isTimestampColumn &&
			!customInputColumnsSet.has(selectedColumn) &&
			!typedValueError &&
			!filtered.some(v => v.toLowerCase() === trimmedSearch.toLowerCase())
		) {
			return [trimmedSearch, ...filtered]
		}
		return filtered
	}, [
		selectedColumn,
		searchInput,
		getColumnValues,
		columns,
		customInputColumnsSet,
		filters,
		serverColumnActive,
		serverValues,
		typedValueError,
	])

	// Render window over the value options, grown on scroll (see VirtualizedValueList.onReachEnd).
	const [visibleValueLimit, setVisibleValueLimit] = React.useState(VALUE_OPTIONS_PAGE_SIZE)
	// Reset to the first page during render (no effect) when the column or query changes.
	const valueWindowKey = `${selectedColumn}\u0000${searchInput}`
	const [prevValueWindowKey, setPrevValueWindowKey] = React.useState(valueWindowKey)
	if (valueWindowKey !== prevValueWindowKey) {
		setPrevValueWindowKey(valueWindowKey)
		setVisibleValueLimit(VALUE_OPTIONS_PAGE_SIZE)
		// Clear any stale validation error when the column or typed value changes.
		setCustomInputError(null)
	}
	// Server columns render everything loaded and grow via fetchNextPage; client columns grow the in-memory
	// render window on scroll.
	const currentValues = React.useMemo(
		() => (serverColumnActive ? currentValuesFull : currentValuesFull.slice(0, visibleValueLimit)),
		[serverColumnActive, currentValuesFull, visibleValueLimit],
	)
	const hiddenValuesCount = serverColumnActive
		? Math.max(0, serverTotalCount - serverValues.length)
		: currentValuesFull.length - currentValues.length
	const loadMoreValues = React.useCallback(() => {
		if (serverColumnActive) {
			if (columnValuesQuery.hasNextPage && !columnValuesQuery.isFetchingNextPage) {
				void columnValuesQuery.fetchNextPage()
			}
			return
		}
		setVisibleValueLimit(prev => (prev >= currentValuesFull.length ? prev : prev + VALUE_OPTIONS_PAGE_SIZE))
	}, [serverColumnActive, columnValuesQuery, currentValuesFull.length])

	// O(1) per-row selected check instead of scanning filters for every rendered option.
	const selectedValuesForColumn = React.useMemo(
		() => new Set(filters.filter(f => f.column === selectedColumn).map(f => f.value)),
		[filters, selectedColumn],
	)
	const selectedColumnIsTimestamp = columns.find(col => col.id === selectedColumn)?.isTimestamp
	const selectedColumnIsRadio = columns.find(col => col.id === selectedColumn)?.isRadio

	const getColumnLabel = (columnId: string) => {
		const column = columns.find(col => col.id === columnId)
		if (column) {
			return column.label
		}

		// Fallback: If column not found, try to generate a friendly name
		// This handles the brief moment during state updates
		if (columnId.startsWith('state.')) {
			const fieldId = columnId.replace('state.', '')
			// Convert snake_case to Title Case
			return fieldId
				.split('_')
				.map(word => word.charAt(0).toUpperCase() + word.slice(1))
				.join(' ')
		}

		if (columnId.startsWith('metadata.')) {
			const fieldId = columnId.replace('metadata.', '')
			// Convert snake_case to Title Case
			return fieldId
				.split('_')
				.map(word => word.charAt(0).toUpperCase() + word.slice(1))
				.join(' ')
		}

		return columnId
	}

	const getSortedColumns = React.useCallback((cols: typeof columns) => {
		const base = cols.filter(col => !col.id.includes('.'))
		const state = cols.filter(col => col.id.startsWith('state.'))
		const metadata = cols.filter(col => col.id.startsWith('metadata.'))
		const other = cols.filter(
			col => col.id.includes('.') && !col.id.startsWith('state.') && !col.id.startsWith('metadata.'),
		)
		return [...base, ...state, ...metadata, ...other]
	}, [])

	const currentColumnDataType = React.useMemo(() => {
		if (!selectedColumn) {
			return undefined
		}
		return columns.find(col => col.id.toLowerCase() === selectedColumn.toLowerCase())?.dataType
	}, [selectedColumn, columns])

	const filteredColumns = React.useMemo(() => {
		const filtered =
			searchInput && view === 'columns'
				? columns.filter(col => col.label.toLowerCase().includes(searchInput.toLowerCase()))
				: columns

		return getSortedColumns(filtered)
	}, [columns, searchInput, view, getSortedColumns])

	const suggestions = React.useMemo(() => getSuggestions(), [getSuggestions])

	// Click on filter badge to edit
	const handleFilterBadgeClick = (columnId: string, e: React.MouseEvent | React.KeyboardEvent) => {
		// Standalone chips have nothing to edit — a click just removes them.
		if (standaloneColumnsSet.has(columnId)) {
			onFiltersChange?.(filters.filter(f => f.column !== columnId))
			setIsOpen(false)
			setView('columns')
			setSelectedColumn(null)
			return
		}

		// Calculate the offset of the clicked badge relative to the container
		if (containerRef.current && e.currentTarget) {
			const containerRect = containerRef.current.getBoundingClientRect()
			const badgeRect = (e.currentTarget as HTMLElement).getBoundingClientRect()
			setClickedBadgeOffset(badgeRect.left - containerRect.left)
		}
		setSelectedColumn(columnId)
		setView('values')
		setIsOpen(true)
		setSearchInput('')
		const existingFilter = filters.find(f => f.column === columnId)
		if (existingFilter) {
			setSelectedOperator(existingFilter.operator)
		} else {
			setSelectedOperator(null)
		}
	}
	const handleFilterValueClick = (columnId: string, value: string, e: React.MouseEvent | React.KeyboardEvent) => {
		e.stopPropagation()

		// Calculate the offset of the clicked value relative to the container
		if (containerRef.current && e.currentTarget) {
			const containerRect = containerRef.current.getBoundingClientRect()
			const badgeRect = (e.currentTarget as HTMLElement).getBoundingClientRect()
			setClickedBadgeOffset(badgeRect.left - containerRect.left)
		}

		// Select the value and open the dropdown for that column
		setSelectedFilterValue({ column: columnId, value })
		setSelectedColumn(columnId)
		setView('values')
		setIsOpen(true)
		setSearchInput('')
		const existingFilter = filters.find(f => f.column === columnId)
		if (existingFilter) {
			setSelectedOperator(existingFilter.operator)
		} else {
			setSelectedOperator(null)
		}

		// If this is a timestamp/duration column with a custom value (ending in 'ms'), pre-fill the custom time inputs
		const columnConfig = columns.find(col => col.id === columnId)
		if (columnConfig?.isTimestamp && value && typeof value === 'string' && value.endsWith('ms')) {
			const ms = Number.parseInt(value.replace('ms', ''), 10)
			if (!Number.isNaN(ms)) {
				const oneMin = 60 * 1000
				const oneHour = 60 * oneMin
				const oneDay = 24 * oneHour

				if (ms % oneDay === 0) {
					setCustomTimeValue(ms / oneDay)
					setCustomTimeUnit('days')
				} else if (ms % oneHour === 0) {
					setCustomTimeValue(ms / oneHour)
					setCustomTimeUnit('hours')
				} else {
					// Default to minutes, handle non-exact division if necessary or just round/float
					// For simple cases, integer division for minutes:
					setCustomTimeValue(ms / oneMin)
					setCustomTimeUnit('minutes')
				}
				// Also open the custom time modal
				setShowCustomTimeModal(true)
			}
		} else {
			// Reset if not a custom ms value
			setCustomTimeValue('')
			setCustomTimeUnit('minutes')
			// Ensure modal is closed for standard values (so user sees the list "1 min", "5 min", etc.)
			// or if they want to switch to custom, they can click "Custom"
			setShowCustomTimeModal(false)
		}

		// Handle custom input columns (those with simple text input like range in some contexts)
		if (customInputColumnsSet.has(columnId)) {
			setRangeInputValue(value)
		} else {
			setRangeInputValue('')
		}

		// Focus the input so backspace works
		setTimeout(() => {
			inputRef.current?.focus()
		}, 10)
	}

	const renderInputContainer = (isInline = false) => {
		const useCompactInput = filters.length > 0

		return (
			<div
				className={cn('flex flex-row min-w-0', isInline || useCompactInput ? 'w-auto shrink-0' : 'flex-1')}
				key='search-input-container'>
				{/* Show selected column indicator when in values view but NO filters exist yet */}
				{selectedColumn &&
					(view === 'operators' || view === 'values') &&
					!filters.some(f => f.column === selectedColumn) && (
						<div className='inline-flex items-center px-1 gap-1 py-0.5 h-auto min-h-6 text-control font-semibold shrink-0'>
							<span className='whitespace-nowrap'>{getColumnLabel(selectedColumn)}</span>
							{isEqualsOnlyColumn(selectedColumn) ? (
								<span>:</span>
							) : view === 'values' &&
								(!columns.find(col => col.id === selectedColumn)?.isTimestamp ||
									(selectedOperator && selectedOperator !== 'timeRange') ||
									(columnOperators[selectedColumn]?.[0] && columnOperators[selectedColumn]?.[0] !== 'timeRange')) ? (
								<span className='text-control text-foreground font-medium px-1'>
									{(() => {
										const operator = selectedOperator || columnOperators[selectedColumn]?.[0] || 'equals'
										const labels = {
											contains: 'contains',
											equals: '=',
											notEquals: '!=',
											is_null: 'is null',
											is_not_null: 'is not null',
											timeRange: 'in last',
											'<': '<',
											'<=': '<=',
											'>': '>',
											'>=': '>=',
										}
										return labels[operator]
									})()}
								</span>
							) : null}
						</div>
					)}
				<input
					ref={inputRef}
					type='text'
					data-testid={searchInputTestId}
					placeholder={filters.filter(f => f.value !== '').length === 0 ? placeholder : ''}
					value={searchInput}
					onChange={handleSearchChange}
					onPaste={handlePaste}
					onKeyDown={handleKeyDown}
					onClick={handleInputClick}
					onFocus={handleInputClick}
					disabled={disabled}
					size={isInline || useCompactInput ? Math.max(1, searchInput.length) : undefined}
					className={cn(
						'h-(--height-control) bg-transparent border-none outline-none text-control placeholder:text-control disabled:cursor-not-allowed',
						isInline || useCompactInput ? 'px-0 min-w-1 w-auto' : 'w-full px-1 min-w-0',
					)}
				/>
			</div>
		)
	}

	// Callers that want the bar to fill its container pass [--search-width:100%]. A percentage floor
	// would resolve against the shrink-to-fit root (its own content), so those get w-full instead.
	const isFullWidth = className?.includes('[--search-width:100%]')

	return (
		<div
			data-testid={containerTestId}
			className={cn('relative max-w-full flex items-center gap-2', isFullWidth ? 'w-full' : 'w-fit', className)}
			ref={containerRef}>
			<div className={cn('relative flex-1', isFullWidth ? 'min-w-0' : 'min-w-(--search-width)')}>
				<div
					className={cn(
						'flex items-center border rounded-md px-2 pr-10 bg-card transition-colors min-h-(--search-height) flex-wrap',
						disabled ? 'cursor-not-allowed opacity-50' : 'cursor-text hover:border-ring/50',
						isOpen && 'border-ring/50',
					)}
					onClick={e => {
						// Check if clicking on a filter value span
						const target = e.target as HTMLElement
						const isFilterValue = target.closest('span[data-filter-value]')

						if (isFilterValue) {
							// Don't do anything, let the span's onClick handle it
							return
						}

						handleInputClick()
					}}
					onKeyDown={e => {
						if (e.key === 'Enter' || e.key === ' ') {
							inputRef.current?.focus()
						}
					}}>
					{Object.entries(
						filters.reduce<Record<string, string[]>>((acc, f) => {
							if (!acc[f.column]) {
								acc[f.column] = []
							}
							acc[f.column].push(f.value)
							return acc
						}, {}),
					).map(([columnId, values], index, arr) => {
						const isNotLast = index < arr.length - 1
						const nextColumnId = isNotLast ? arr[index + 1][0] : null
						const logicOperator = nextColumnId ? getLogicOperator(columnId, nextColumnId) : null
						const showLogic = arr.length > 1 && isNotLast

						// Get the operator for this column
						const columnFilters = filters.filter(f => f.column === columnId)
						const operator = columnFilters[0]?.operator || 'contains'

						const getOperatorLabel = (operator: FilterOption['operator']) => {
							const labels = {
								contains: 'contains',
								equals: '=',
								notEquals: '!=',
								is_null: 'is null',
								is_not_null: 'is not null',
								timeRange: 'in last',
								'<': '<',
								'<=': '<=',
								'>': '>',
								'>=': '>=',
							}
							return labels[operator]
						}

						// Standalone chips show only their label and remove on click (no operator/value parts).
						if (standaloneColumnsSet.has(columnId)) {
							return (
								<React.Fragment key={`filter-${columnId}`}>
									<div
										data-filter-badge
										className='flex flex-wrap items-center gap-1 px-1 py-0.5 h-auto min-h-6 cursor-pointer rounded hover:bg-accent/50 transition-colors'
										onClick={e => {
											e.stopPropagation()
											handleFilterBadgeClick(columnId, e)
										}}
										onKeyDown={e => {
											if (e.key === 'Enter' || e.key === ' ') {
												e.stopPropagation()
												handleFilterBadgeClick(columnId, e)
											}
										}}>
										<span className='text-control font-semibold break-all'>{getColumnLabel(columnId)}</span>
										<X className='size-(--icon-standalone) text-muted-foreground' />
									</div>
								</React.Fragment>
							)
						}

						return (
							<React.Fragment key={`filter-${columnId}`}>
								<div
									data-filter-badge
									data-testid={
										containerTestId ? `${containerTestId}-filter-${filterValueTestIdSegment(columnId)}` : undefined
									}
									className='flex min-w-0 max-w-full flex-nowrap items-center gap-1 overflow-hidden px-1 py-0.5 h-auto min-h-6 cursor-pointer rounded hover:bg-accent/50 transition-colors'
									onClick={e => {
										e.stopPropagation()
										handleFilterBadgeClick(columnId, e)
									}}
									onKeyDown={e => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.stopPropagation()
											handleFilterBadgeClick(columnId, e)
										}
									}}>
									<span className='text-control font-semibold shrink-0 whitespace-nowrap'>
										{getColumnLabel(columnId)}
									</span>

									{enableOperatorChange &&
									columnOperators[columnId] &&
									columnOperators[columnId].length > 1 &&
									!isEqualsOnlyColumn(columnId) ? (
										<DropdownMenu
											open={editingOperator?.column === columnId}
											onOpenChange={open => {
												if (!open) {
													setEditingOperator(null)
												}
											}}>
											<DropdownMenuTrigger asChild>
												<Button
													variant='ghost'
													size='sm'
													onClick={e => {
														e.stopPropagation()
														e.preventDefault()

														// Save the current badge's visual position so if the operator change
														// forces the values menu to reopen natively, it aligns directly under this badge!
														if (containerRef.current && e.currentTarget) {
															const containerRect = containerRef.current.getBoundingClientRect()
															// Walk up to the parent wrapper div badge to grab its bounding box
															const badgeDiv = (e.currentTarget as HTMLElement).closest('div[data-filter-badge]')
															if (badgeDiv) {
																const badgeRect = badgeDiv.getBoundingClientRect()
																setClickedBadgeOffset(badgeRect.left - containerRect.left)
															}
														}

														setEditingOperator({ column: columnId, index: 0 })
														setIsOpen(false)
													}}
													onMouseDown={e => {
														e.stopPropagation()
														e.preventDefault()
													}}
													className='inline-flex items-center h-6 text-control leading-none text-foreground font-medium hover:bg-muted/50 px-1 rounded-none transition-colors'>
													{getOperatorLabel(operator)}
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent
												align='start'
												className='w-32'
												onPointerDown={e => e.stopPropagation()}
												onMouseDown={e => e.stopPropagation()}
												onCloseAutoFocus={e => e.preventDefault()}>
												{(() => {
													const allowedOperators = columnOperators[columnId] || ['contains', 'is_null', 'is_not_null']
													const operatorDisplayMap: Record<FilterOption['operator'], string> = {
														contains: 'contains',
														equals: '=',
														notEquals: '!=',
														is_null: 'is null',
														is_not_null: 'is not null',
														timeRange: 'in last',
														'<': '<',
														'<=': '<=',
														'>': '>',
														'>=': '>=',
													}

													return allowedOperators.map(op => (
														<DropdownMenuItem
															key={op}
															onClick={e => {
																e.stopPropagation()
																handleOperatorChange(columnId, op)
															}}
															className={cn('cursor-pointer', operator === op && 'bg-accent')}>
															<Check
																className={cn(
																	'size-(--icon-standalone) mr-2',
																	operator === op ? 'opacity-100' : 'opacity-0',
																)}
															/>
															{operatorDisplayMap[op]}
														</DropdownMenuItem>
													))
												})()}
											</DropdownMenuContent>
										</DropdownMenu>
									) : isEqualsOnlyColumn(columnId) ? (
										// Equals-only columns (id/uid/Project) have no operator to show — render `key: value`.
										<span className='text-control shrink-0'>:</span>
									) : (
										<span className='text-control text-foreground font-medium px-1'>{getOperatorLabel(operator)}</span>
									)}

									{(() => {
										const columnConfig = columns.find(col => col.id === columnId)
										// `val` is already the display string produced by getColumnValues (formatValue
										// applied once there) — reformatting here would double-format it.
										const toDisplay = (val: string) => {
											if (!columnConfig?.isTimestamp) {
												return val
											}
											const timeOption = TIME_RANGE_OPTIONS.find(opt => opt.value === val)
											return timeOption ? timeOption.label : convertMsToReadable(val)
										}
										const displayValues = values.filter(v => v !== '')
										if (displayValues.length === 0) {
											return null
										}
										const summarize = displayValues.length > FILTER_SUMMARY_VALUE_LIMIT
										return (
											<Tooltip>
												<TooltipTrigger asChild focusable={false}>
													<div className='flex min-w-[6ch] flex-1 flex-nowrap items-center gap-[0.5] overflow-hidden'>
														{summarize ? (
															<span className='text-control text-foreground font-semibold px-1 min-w-0 truncate whitespace-nowrap'>
																{displayValues.length} values
															</span>
														) : (
															displayValues.map((val, idx) => {
																const isSelected =
																	selectedFilterValue?.column === columnId && selectedFilterValue?.value === val
																const decoration = columnValueDecorations?.[columnId]?.[val]

																return (
																	<React.Fragment key={val}>
																		{idx > 0 && <span className='text-control shrink-0'>,</span>}
																		<span
																			data-filter-value
																			className={cn(
																				'text-control text-foreground font-semibold hover:bg-accent/50 px-1 rounded cursor-pointer transition-colors min-w-0 max-w-40 truncate whitespace-nowrap',
																				decoration?.icon && 'inline-flex items-center gap-1',
																				isSelected && 'bg-muted ring-2 ring-ring',
																			)}
																			onClick={e => handleFilterValueClick(columnId, val, e)}
																			onKeyDown={e => {
																				if (e.key === 'Enter' || e.key === ' ') {
																					handleFilterValueClick(columnId, val, e)
																				}
																			}}>
																			{decoration?.icon && (
																				<DynamicIcon
																					name={decoration.icon as IconName}
																					className='size-(--icon-standalone) shrink-0'
																					style={{ color: decoration.color }}
																				/>
																			)}
																			{toDisplay(val)}
																		</span>
																	</React.Fragment>
																)
															})
														)}
													</div>
												</TooltipTrigger>
												<TooltipContent side='bottom' align='start' className='max-w-xl'>
													{displayValues.map(toDisplay).join(', ')}
												</TooltipContent>
											</Tooltip>
										)
									})()}
								</div>

								{/* Inject inline input BEFORE logic operator */}
								{selectedColumn === columnId && index < arr.length - 1 && renderInputContainer(true)}

								{/* Show OR/AND logic operator AFTER the badge */}
								{showLogic && logicOperator && (
									<div className='flex items-center ml-1'>
										<Select
											disabled={!allowLogicOperatorChange}
											value={logicOperator}
											onValueChange={val => {
												if (!allowLogicOperatorChange || !nextColumnId) {
													return
												}
												if (val !== logicOperator) {
													toggleLogicOperator(columnId, nextColumnId)
												}
											}}>
											<SelectTrigger
												className={cn(
													'w-auto h-6 px-2 py-0 text-control font-semibold rounded uppercase border-0 ring-0 focus:ring-0 focus-visible:ring-0 outline-none focus:outline-none shadow-none focus:shadow-none bg-transparent [&>svg]:hidden',
													logicOperator === 'OR' ? 'text-warning' : 'text-muted-foreground',
													allowLogicOperatorChange && 'hover:bg-accent cursor-pointer',
													!allowLogicOperatorChange && 'cursor-default opacity-70',
												)}
												tabIndex={allowLogicOperatorChange ? 0 : -1}
												onPointerDown={e => e.stopPropagation()}
												onMouseDown={e => e.stopPropagation()}>
												<span>{logicOperator}</span>
											</SelectTrigger>
											<SelectContent onPointerDown={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
												<SelectItem value='AND'>AND</SelectItem>
												<SelectItem value='OR'>OR</SelectItem>
											</SelectContent>
										</Select>
									</div>
								)}
							</React.Fragment>
						)
					})}

					{(() => {
						if (!isOpen && filters.length > 0) {
							return null
						}

						const groupedCols = filters.reduce<Record<string, string[]>>((acc, f) => {
							if (!acc[f.column]) {
								acc[f.column] = []
							}
							acc[f.column].push(f.value)
							return acc
						}, {})
						const columnsWithFilters = Object.keys(groupedCols)

						const isSelectedColumnLast =
							columnsWithFilters.length > 0 && selectedColumn === columnsWithFilters[columnsWithFilters.length - 1]
						const isSelectedColumnNotPresent = selectedColumn && !columnsWithFilters.includes(selectedColumn)

						if (
							!selectedColumn ||
							isSelectedColumnLast ||
							isSelectedColumnNotPresent ||
							columnsWithFilters.length === 0
						) {
							return renderInputContainer(false)
						}
						return (
							<div className='flex flex-row flex-1 min-w-0 pointer-events-none opacity-0 h-0 overflow-hidden'>
								<input disabled className='w-full px-1' />
							</div>
						)
					})()}
				</div>
				{showClearButton && filters.length > 0 && !disabled && (
					<Button
						variant='ghost'
						size='icon'
						onClick={handleClear}
						data-testid={clearButtonTestId}
						className='absolute right-1 top-1/2 -translate-y-1/2 size-6 bg-transparent hover:bg-transparent active:bg-transparent focus-visible:bg-transparent z-10'>
						<X className='size-(--icon-standalone)' />
					</Button>
				)}
			</div>

			{/* Filtered Count Indicator */}
			{filters.length > 0 && (filteredCount !== undefined || totalCount !== undefined || isLoading) && (
				<FilteredCountIndicator
					testId={filteredCountTestId}
					filteredCount={filteredCount}
					totalCount={totalCount}
					isLoading={isLoading}
					label={filteredCountLabel}
					className='h-(--height-control)'
				/>
			)}

			{/* Dropdown Menu */}
			{isOpen &&
				!disabled &&
				hasColumns &&
				createPortal(
					<div
						ref={dropdownRef}
						data-prevent-close='true'
						data-testid={filterPopoverTestId}
						className='top-full mt-1 bg-popover border rounded-lg shadow-levitating overflow-hidden flex flex-col'
						style={(() => {
							const rect = containerRef.current?.getBoundingClientRect()
							const dropdownW = SEARCH_DROPDOWN_WIDTH_PX
							const rawLeft = (rect?.left ?? 0) + (clickedBadgeOffset !== null ? clickedBadgeOffset : inputOffset) - 8
							const leftPos = Math.max(8, Math.min(rawLeft, window.innerWidth - dropdownW - 8))
							const inputBottom = rect?.bottom ?? 0
							const inputTop = rect?.top ?? 0
							const dropdownH = insideModal ? 350 : 400
							const spaceBelow = window.innerHeight - inputBottom - 8
							const flipUp = rect != null && spaceBelow < dropdownH && inputTop > spaceBelow
							return {
								width: `${SEARCH_DROPDOWN_WIDTH_PX}px`,
								position: 'fixed' as const,
								...(flipUp
									? { bottom: `${window.innerHeight - inputTop + 4}px`, top: 'auto' as const }
									: { top: `${inputBottom + 4}px`, bottom: 'auto' as const }),
								left: `${leftPos}px`,
								zIndex: 99999,
								pointerEvents: 'auto' as const,
								maxHeight: `${Math.min(dropdownH, flipUp ? inputTop - 8 : spaceBelow)}px`,
							}
						})()}
						onWheel={e => {
							// Stop wheel events from propagating to parent
							e.stopPropagation()
						}}
						onTouchMove={e => {
							// Stop touch events from propagating to parent (for mobile)
							e.stopPropagation()
						}}>
						{/* Suggestions View */}
						{view === 'suggestions' ? (
							<div className={`${insideModal ? 'max-h-[350px]' : 'max-h-[400px]'} scrollable flex flex-col`}>
								<div className='sticky top-0 bg-[var(--search-header-bg)] px-3 py-2 border-b'>
									<p className='text-control font-semibold text-muted-foreground'>Suggestions</p>
								</div>
								<div className='scrollable overflow-y-auto py-1' onMouseLeave={handleMouseLeave}>
									{suggestions.length === 0 ? (
										<div className='px-4 py-8 text-center text-control text-muted-foreground'>
											{suggestionsFetching ? 'Searching…' : 'No matches found'}
										</div>
									) : (
										suggestions.map((suggestion, idx) => (
											<Button
												key={`suggestion-${suggestion.column}-${suggestion.value}`}
												variant='ghost'
												onClick={() => handleSuggestionClick(suggestion)}
												onMouseEnter={() => handleMouseEnter(idx)}
												className={getButtonClassName({
													isFocused: focusedIndex === idx,
													additionalClasses: 'py-1.5 h-auto',
												})}>
												<div className='min-w-0 flex-1 text-left'>
													<span className='text-foreground text-control break-all'>
														{suggestion.isColumnMatch
															? suggestion.columnLabel
															: `${suggestion.columnLabel} : ${suggestion.value}`}
													</span>
												</div>
											</Button>
										))
									)}
								</div>
							</div>
						) : view === 'operators' && selectedColumn ? (
							<div
								className={`${insideModal ? 'max-h-[350px]' : 'max-h-[400px]'} scrollable overflow-y-auto flex flex-col`}
								onMouseLeave={handleMouseLeave}>
								<div className='sticky top-0 bg-[var(--search-header-bg)] px-3 py-2 border-b flex items-center gap-2'>
									<Button
										variant='ghost'
										size='icon-sm'
										onClick={handleBack}
										className='hover:bg-accent rounded size-6 text-control'>
										<ChevronLeft className='size-(--icon-standalone)' />
									</Button>
									<p className='text-control font-semibold text-muted-foreground'>
										Select operator for {getColumnLabel(selectedColumn)}
									</p>
								</div>
								<div className='scrollable overflow-y-auto py-1'>
									{(() => {
										const allowedOperators = columnOperators[selectedColumn] || ['contains', 'is_null', 'is_not_null']
										const filteredOperators = allowedOperators.filter(op => {
											if (!searchInput) {
												return true
											}

											const operatorDisplayMap: Record<FilterOption['operator'], string> = {
												contains: 'contains',
												equals: '=',
												notEquals: '!=',
												is_null: 'is null',
												is_not_null: 'is not null',
												timeRange: 'in last',
												'<': '<',
												'<=': '<=',
												'>': '>',
												'>=': '>=',
											}

											const displayText = operatorDisplayMap[op] || op
											return displayText.toLowerCase().includes(searchInput.toLowerCase())
										})
										const operatorDisplayMap: Record<FilterOption['operator'], string> = {
											contains: 'contains',
											equals: '=',
											notEquals: '!=',
											is_null: 'is null',
											is_not_null: 'is not null',
											timeRange: 'in last',
											'<': '<',
											'<=': '<=',
											'>': '>',
											'>=': '>=',
										}
										if (filteredOperators.length === 0) {
											return (
												<div className='px-4 py-8 text-center text-control text-muted-foreground'>
													No operators found
												</div>
											)
										}
										return filteredOperators.map((op, idx) => {
											// Check if this operator is currently selected for this column
											// Prefer explicit selection state if current operator is being picked, else use existing filters
											const isSelected = selectedOperator
												? selectedOperator === op
												: filters.some(f => f.column === selectedColumn && f.operator === op)

											return (
												<Button
													key={op}
													variant='ghost'
													onClick={() => {
														if (selectedColumn) {
															handleOperatorChange(selectedColumn, op)
														}
													}}
													onMouseEnter={() => handleMouseEnter(idx)}
													className={cn(
														'w-full justify-between items-center group h-9 px-3 text-control',
														focusedIndex === idx || isSelected ? 'bg-accent' : 'bg-transparent text-muted-foreground',
													)}>
													<span className='text-foreground text-control'>{operatorDisplayMap[op]}</span>
													{isSelected && <Check className='size-(--icon-standalone) text-foreground ml-2' />}
												</Button>
											)
										})
									})()}
								</div>
							</div>
						) : view === 'columns' ? (
							<div
								className={`${insideModal ? 'max-h-[350px]' : 'max-h-[400px]'} scrollable overflow-y-auto flex flex-col`}
								onMouseLeave={handleMouseLeave}>
								{filteredColumns.length === 0 ? (
									<div className='px-4 py-8 text-center text-control text-muted-foreground'>No columns found</div>
								) : (
									<div className='py-1'>
										{(() => {
											// Calculate sections
											const baseColumns = filteredColumns.filter(col => !col.id.includes('.'))
											const stateColumns = filteredColumns.filter(col => col.id.startsWith('state.'))
											const metadataColumns = filteredColumns.filter(col => col.id.startsWith('metadata.'))

											let globalIndex = 0

											return (
												<>
													{/* Base Columns */}
													{baseColumns.length > 0 &&
														baseColumns.map(column => {
															const currentIndex = globalIndex++
															return (
																<Button
																	key={column.id}
																	variant='ghost'
																	data-testid={resolveFilterColumnButtonTestId(column.id)}
																	onClick={() => handleColumnClick(column.id)}
																	onMouseEnter={() => handleMouseEnter(currentIndex)}
																	className={getButtonClassName({ isFocused: focusedIndex === currentIndex })}>
																	<span className='text-foreground text-control'>{column.label}</span>
																	{!standaloneColumnsSet.has(column.id) && (
																		<ChevronDown className='size-(--icon-standalone) text-muted-foreground group-hover:text-foreground -rotate-90' />
																	)}
																</Button>
															)
														})}

													{/* State Columns */}
													{stateColumns.length > 0 && (
														<>
															<div className='px-4 py-2 bg-[var(--search-header-bg)]'>
																<span className='text-control font-bold text-foreground opacity-50'>Device State</span>
															</div>
															{stateColumns.map(column => {
																const currentIndex = globalIndex++
																return (
																	<Button
																		key={column.id}
																		variant='ghost'
																		data-testid={resolveFilterColumnButtonTestId(column.id)}
																		onClick={() => handleColumnClick(column.id)}
																		onMouseEnter={() => handleMouseEnter(currentIndex)}
																		className={getButtonClassName({ isFocused: focusedIndex === currentIndex })}>
																		<span className='text-foreground text-control'>{column.label}</span>
																		{!standaloneColumnsSet.has(column.id) && (
																			<ChevronDown className='size-(--icon-standalone) text-muted-foreground group-hover:text-foreground -rotate-90' />
																		)}
																	</Button>
																)
															})}
														</>
													)}

													{/* Metadata Columns */}
													{metadataColumns.length > 0 && (
														<>
															<div className='px-4 py-2 bg-[var(--search-header-bg)]'>
																<span className='text-control font-bold text-foreground opacity-50'>Metadata</span>
															</div>
															{metadataColumns.map(column => {
																const currentIndex = globalIndex++
																return (
																	<Button
																		key={column.id}
																		variant='ghost'
																		data-testid={resolveFilterColumnButtonTestId(column.id)}
																		onClick={() => handleColumnClick(column.id)}
																		onMouseEnter={() => handleMouseEnter(currentIndex)}
																		className={getButtonClassName({ isFocused: focusedIndex === currentIndex })}>
																		<span className='text-foreground text-control'>
																			{column.label.replace('Metadata : ', '')}
																		</span>
																		{!standaloneColumnsSet.has(column.id) && (
																			<ChevronDown className='size-(--icon-standalone) text-muted-foreground group-hover:text-foreground -rotate-90' />
																		)}
																	</Button>
																)
															})}
														</>
													)}
												</>
											)
										})()}
									</div>
								)}
							</div>
						) : (
							<div
								className={`${insideModal ? 'max-h-[350px]' : 'max-h-[400px]'} scrollable overflow-y-auto flex flex-col`}
								onMouseLeave={handleMouseLeave}>
								<div className='sticky top-0 z-10 bg-[var(--search-header-bg)] border-b'>
									<div className='flex items-center gap-2 px-3 py-2.5'>
										<Button
											variant='ghost'
											size='icon-sm'
											onClick={handleBack}
											className='hover:bg-accent rounded size-6 shrink-0'>
											<ChevronLeft className='size-(--icon-standalone)' />
										</Button>
										<div className='flex w-full justify-between items-center gap-3'>
											<p className='text-control font-semibold text-muted-foreground whitespace-nowrap'>
												{filters.filter(f => f.column === selectedColumn).length} selected
											</p>
											{/* Hide Select All / Clear All for timestamp columns */}
											{!columns.find(col => col.id === selectedColumn)?.isTimestamp &&
												!customInputColumnsSet.has(selectedColumn || '') && (
													<div className='flex items-center gap-1.5 shrink-0'>
														{/* Select All / Clear All buttons */}
														<Button
															size='sm'
															variant='link'
															onClick={handleSelectAll}
															disabled={
																serverColumnActive
																	? serverValuesLoading || serverValues.length === 0
																	: currentValues.length === 0
															}
															className='text-control text-foreground size-fit'>
															Select All
														</Button>
														<Separator orientation='vertical' className='h-4!' />
														<Button
															size='sm'
															variant='link'
															onClick={handleClearAll}
															className='text-control text-destructive size-fit'>
															Clear All
														</Button>
													</div>
												)}
										</div>
									</div>
									{serverColumnActive &&
										filters.filter(f => f.column === selectedColumn).length >= SELECT_ALL_MAX &&
										serverTotalCount > SELECT_ALL_MAX && (
											<div className='flex items-center border-t bg-status-warning-bg px-3 py-1.5 text-status-warning-text'>
												<span className='text-[11px] leading-tight'>
													First {SELECT_ALL_MAX.toLocaleString()} selected — scroll to load and select more
												</span>
											</div>
										)}
								</div>
								{/* Live validation for a free-typed value in the suggestion list (e.g. non-numeric UID). */}
								{typedValueError && !customInputColumnsSet.has(selectedColumn || '') && (
									<div className='flex items-center border-b px-3 py-1.5 text-[11px] text-destructive'>
										<span>{typedValueError}</span>
									</div>
								)}
								<div className='scrollable overflow-y-auto py-1 z-[999]'>
									{/* Check if current column has range operator */}
									{customInputColumnsSet.has(selectedColumn || '') ? (
										// Show custom input for device state columns
										<div className='px-4 py-4 space-y-3'>
											<div>
												<label htmlFor='custom-value-input' className='text-control font-medium mb-2 block'>
													Enter value for {getColumnLabel(selectedColumn || '')}:
												</label>
												<Input
													ref={customInputRef}
													id='custom-value-input'
													type='text'
													value={rangeInputValue}
													onChange={e => {
														const rawValue = e.target.value
														setRangeInputValue(rawValue)

														let error: string | null = null
														if (currentColumnDataType && rawValue.trim()) {
															error = validateInputValue(rawValue, currentColumnDataType)
														}
														setCustomInputError(error)
													}}
													onKeyDown={e => {
														e.stopPropagation()

														if (e.key === 'Enter' && rangeInputValue.trim() && !customInputError) {
															const success = handleValueClick(rangeInputValue.trim())
															if (success) {
																setRangeInputValue('')
																// Keep dropdown open, show columns menu
																setView('columns')
																setSelectedColumn(null)
																setSelectedOperator(null)
																setShowCustomInputModal(false)
																setFocusedIndex(0)
															}
														}
													}}
													onClick={e => e.stopPropagation()}
													onFocus={e => e.stopPropagation()}
													placeholder='Enter value'
													className='w-full h-(--height-control) text-control z-[999]'
													autoFocus={showCustomInputModal}
												/>
												{customInputError && <p className='text-control text-destructive mt-1.5'>{customInputError}</p>}
											</div>

											<Button
												variant='default'
												onClick={() => {
													if (rangeInputValue.trim()) {
														const success = handleValueClick(rangeInputValue.trim())
														if (success) {
															setRangeInputValue('')
															// Keep dropdown open, show columns menu
															setView('columns')
															setSelectedColumn(null)
															setSelectedOperator(null)
															setShowCustomInputModal(false)
															setFocusedIndex(0)
														}
													}
												}}
												disabled={!rangeInputValue.trim() || !!customInputError}
												className='w-full h-(--height-control) text-control mt-4'>
												Add Filter
											</Button>
										</div>
									) : showCustomTimeModal ? (
										<div className='space-y-4 p-2'>
											<div>
												<label htmlFor='custom-time-value' className='text-control font-medium mb-2 block'>
													Time Value
												</label>
												<Input
													id='custom-time-value'
													type='number'
													min={1}
													placeholder='Enter value'
													value={customTimeValue}
													onChange={e => {
														const val = e.target.value
														setCustomTimeValue(val === '' ? '' : Number(val))
													}}
													onKeyDown={e => {
														e.stopPropagation()
														if (e.key === 'Enter' && isCustomTimeValid) {
															handleValueClick(String(customTimeValue))
															setCustomTimeValue('')
															setIsOpen(false)
															setView('columns')
															setSelectedColumn(null)
															setSelectedOperator(null)
															setShowCustomInputModal(false)
														}
													}}
													onClick={e => e.stopPropagation()}
													onFocus={e => e.stopPropagation()}
													className='w-full h-(--height-control) text-control'
													autoFocus={showCustomTimeModal}
													aria-invalid={customTimeValue !== '' && !isCustomTimeValid}
													data-testid='filter-custom-time-value'
												/>
												{customTimeValue !== '' && !isCustomTimeValid && (
													<p className='text-control text-destructive mt-1.5' data-testid='filter-custom-time-error'>
														Enter a value greater than 0
													</p>
												)}
											</div>

											<div>
												<label htmlFor='custom-time-unit' className='text-control font-medium mb-2 block'>
													Time Unit
												</label>
												<Select
													value={customTimeUnit}
													onValueChange={val => setCustomTimeUnit(val as 'minutes' | 'hours' | 'days')}>
													<SelectTrigger
														className='w-full h-(--height-control)! text-control py-0'
														onPointerDown={e => e.stopPropagation()}
														onMouseDown={e => e.stopPropagation()}>
														<SelectValue placeholder='Select unit' />
													</SelectTrigger>
													<SelectContent
														className='z-[1000000]!'
														onPointerDown={e => e.stopPropagation()}
														onMouseDown={e => e.stopPropagation()}>
														{TIME_UNIT_OPTIONS.map(unit => (
															<SelectItem key={unit.value} value={unit.value}>
																{unit.label}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</div>

											<Button
												variant='default'
												onClick={handleApplyCustomTime}
												disabled={!isCustomTimeValid}
												data-testid='filter-custom-time-apply'
												className='w-full h-(--height-control) text-control'>
												Apply Custom Time
											</Button>
										</div>
									) : serverValuesLoading ? (
										<ListSkeleton count={6} className='p-4' />
									) : currentValues.length === 0 ? (
										<div className='px-4 py-8 text-center text-control text-muted-foreground'>No values found</div>
									) : (
										<VirtualizedValueList
											values={currentValues}
											isTimestamp={Boolean(selectedColumnIsTimestamp)}
											isRadio={Boolean(selectedColumnIsRadio)}
											selectedValues={selectedValuesForColumn}
											focusedIndex={focusedIndex}
											onValueClick={handleValueClick}
											onMouseEnter={handleMouseEnter}
											getButtonClassName={getButtonClassName}
											hiddenCount={hiddenValuesCount}
											onReachEnd={loadMoreValues}
											insideModal={insideModal}
											getCheckboxTestId={resolveFilterOptionCheckboxTestId}
											valueDecorations={selectedColumn ? columnValueDecorations?.[selectedColumn] : undefined}
										/>
									)}
								</div>
							</div>
						)}
					</div>,
					document.body,
				)}
		</div>
	)
}

/** Sentinel for `resetOn`'s first render, so the initial pass clears like every later change does. */
const NO_RESET_KEY_YET = Symbol('no-reset-key-yet')

/** Chips (AND across columns, OR within one) then the free text, over the loaded rows. What `useSearch` renders. */
export function applySearchToRows<TData>(
	data: TData[],
	filters: FilterOption[],
	searchValue: string,
	searchFields: (keyof TData)[] = [],
): TData[] {
	let result = data

	const effectiveFilters = getEffectiveFilters(filters)
	if (effectiveFilters.length > 0) {
		// Group filters by column
		const groupedFilters = effectiveFilters.reduce<Record<string, FilterOption[]>>((acc, f) => {
			if (!acc[f.column]) {
				acc[f.column] = []
			}
			acc[f.column].push(f)
			return acc
		}, {})

		result = result.filter(item => {
			// AND across columns
			return Object.entries(groupedFilters).every(([column, colFilters]) => {
				const rawValue = item[column as keyof TData]
				const itemValue = String(rawValue ?? '').toLowerCase()

				// OR within same column
				return colFilters.some(filter => {
					const filterValue = filter.value.toLowerCase()
					switch (filter.operator) {
						case 'equals': // Add exact match
							return itemValue === filterValue
						case 'notEquals': // Add this new case
							return itemValue !== filterValue
						case 'contains':
							return itemValue.includes(filterValue)
						case 'is_not_null':
							return itemValue !== '' && itemValue !== 'null' && itemValue !== 'undefined'
						case 'is_null':
							return itemValue === '' || itemValue === 'null' || itemValue === 'undefined'
						case '<':
							return Number.parseFloat(itemValue) < Number.parseFloat(filterValue)
						case '<=':
							return Number.parseFloat(itemValue) <= Number.parseFloat(filterValue)
						case '>':
							return Number.parseFloat(itemValue) > Number.parseFloat(filterValue)
						case '>=':
							return Number.parseFloat(itemValue) >= Number.parseFloat(filterValue)
						case 'timeRange':
							// For timeRange, use rawValue (could be number) to avoid "Invalid Date" from stringified numbers
							return isWithinTimeRange(rawValue as string | number | Date, filter.value)
						default:
							return true
					}
				})
			})
		})
	}

	// Apply free-text search after filters
	if (searchValue) {
		result = result.filter(item => {
			if (searchFields.length === 0) {
				return Object.values(item as Record<string, unknown>).some(value =>
					String(value).toLowerCase().includes(searchValue.toLowerCase()),
				)
			}

			return searchFields.some(field => {
				const value = item[field]
				return String(value).toLowerCase().includes(searchValue.toLowerCase())
			})
		})
	}

	return result
}

// Enhanced hook for managing search with filters
export function useSearch<TData>({
	data,
	searchFields = [],
	columns = [],
	skipUrlFilters = false,
	resetOn,
}: {
	data: TData[]
	searchFields?: (keyof TData)[]
	columns?: Array<{
		id: string
		label: string
		formatValue?: (value: unknown) => string
	}>
	skipUrlFilters?: boolean
	/**
	 * Clears the filters whenever this changes, for a tab that swaps the list under the same component.
	 * Pass the route param the tabs are keyed on. A `key` would do it too, but these pages remount far
	 * more than their filters: page size, selection and every fetch on mount would go with them.
	 */
	resetOn?: string | number
}) {
	const [searchValue, setSearchValue] = React.useState('')
	const [filters, setFilters] = React.useState<FilterOption[]>(() =>
		skipUrlFilters ? [] : getInitialFiltersFromUrl<FilterOption>(),
	)

	// Adjusting state during render, not in an effect: React drops this pass and re-runs before
	// painting, so the table never shows the previous tab's filters.
	const [lastResetOn, setLastResetOn] = React.useState<string | number | symbol | undefined>(NO_RESET_KEY_YET)
	if (resetOn !== undefined && resetOn !== lastResetOn) {
		setLastResetOn(resetOn)
		setFilters([])
	}

	// Reset search and filters when moving to a different section or tab
	// We use session storage to track the last section across page navigations
	React.useEffect(() => {
		const currentPath = window.location.pathname
		const currSection = getRouteSection(currentPath)
		const lastKnownSection = sessionStorage.getItem(SEARCH_SECTION_STORAGE_KEY)

		// If we transitioned to a new section or sub-tab, reset search and filters
		if (lastKnownSection && currSection !== lastKnownSection) {
			setSearchValue('')
			setFilters([])
		}

		sessionStorage.setItem(SEARCH_SECTION_STORAGE_KEY, currSection)
	}, [])

	const filteredData = React.useMemo(
		() => applySearchToRows(data, filters, searchValue, searchFields),
		[data, searchValue, searchFields, filters],
	)

	const clearSearch = () => {
		setSearchValue('')
		setFilters([])
	}

	return {
		searchValue,
		setSearchValue,
		filters,
		setFilters,
		filteredData,
		clearSearch,
		hasSearch: searchValue.length > 0 || getEffectiveFilters(filters).length > 0,
		columns,
	}
}
